import React, { useState, useEffect, useMemo, useRef } from 'react';
import { TournamentCategory, Participant, Match as ApiMatch } from '@/types/api.types';
import { Card, Button, Typography, Spin, Empty, Space, Flex, Tag, message } from 'antd';
import { DownloadOutlined, ReloadOutlined, TrophyOutlined } from '@ant-design/icons';
import { api } from '@/api';
import { TournamentService } from '@/services/tournament.service';
import { logger } from '@/utils/logger';
import { Bracket, Seed, SeedItem, SeedTeam } from '@sportsgram/brackets';
import { StyleSheetManager } from 'styled-components';
import { useAuth } from '@/contexts/AuthContext';

// Импортируем типы из библиотеки
type IRoundProps = Parameters<typeof Bracket>[0]['rounds'][0];
type IRenderSeedProps = Parameters<NonNullable<Parameters<typeof Bracket>[0]['renderSeedComponent']>>[0];

const { Title, Text } = Typography;

interface ClassicBracketViewProps {
  tournamentId: number;
  category: TournamentCategory;
  participants: Participant[];
  tournamentDate?: string; // Дата проведения турнира для проверки возможности перемещения
  bracketsPublished?: boolean; // Флаг публикации сетки (для отображения; drag and drop разрешён и после публикации)
}

// Интерфейс для seed с дополнительными данными
interface CustomSeedData {
  match: (ApiMatch & { __isVirtual?: boolean }) | ({ __isVirtual: true } & Partial<ApiMatch>);
  participant1?: Participant | null;
  participant2?: Participant | null;
  winner?: Participant | null;
}

type SeedWithCustomData = IRenderSeedProps['seed'] & CustomSeedData;

// Функция для фильтрации props, которые не должны попадать в DOM
const shouldForwardProp = (prop: string) => {
  // Фильтруем mobileBreakpoint и другие нестандартные props
  return prop !== 'mobileBreakpoint';
};

export const ClassicBracketView: React.FC<ClassicBracketViewProps> = ({
  tournamentId,
  category,
  participants = [],
  tournamentDate,
  bracketsPublished: _bracketsPublished = false, // принимаем для совместимости; drag and drop разрешён и после публикации
}) => {
  const { user } = useAuth();
  const [matches, setMatches] = useState<ApiMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const bracketRef = useRef<HTMLDivElement>(null);
  
  // Состояния для drag and drop
  const [draggedParticipant, setDraggedParticipant] = useState<{ 
    participant: Participant; 
    matchId: number; 
    position: 1 | 2 
  } | null>(null);
  const [dragOverMatch, setDragOverMatch] = useState<number | null>(null);

  // Проверка прав организатора
  const isUserOrganizer = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'head_manager';
  
  // Проверка, можно ли перемещать участников (до начала турнира; после публикации сетки перемещение разрешено)
  const canMoveParticipants = useMemo(() => {
    if (!tournamentDate) return true;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tournamentStartDate = new Date(tournamentDate);
    tournamentStartDate.setHours(0, 0, 0, 0);
    return today < tournamentStartDate;
  }, [tournamentDate]);

  // Загрузка матчей: в БД matches.categoryId = tournament_categories.id (tc.id), не categories.id
  const loadMatches = async () => {
    const categoryIdForMatches = category?.id;

    if (!tournamentId) {
      logger.warn('No tournamentId provided');
      setLoading(false);
      return;
    }

    if (!categoryIdForMatches) {
      logger.warn('No category.id (tournament_categories.id) for matches, category object:', category);
      setMatches([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/tournaments/${tournamentId}/matches`, {
        params: { categoryId: categoryIdForMatches }
      });

      const matchesData = response.data?.data || response.data || [];

      const categoryMatches = Array.isArray(matchesData)
        ? matchesData.filter((m: ApiMatch) => m.categoryId === categoryIdForMatches)
        : [];
      if (categoryMatches.length === 0 && matchesData.length > 0) {
        logger.warn(`No matches for category.id=${categoryIdForMatches}. Sample match categoryIds:`,
          matchesData.slice(0, 5).map((m: ApiMatch) => m.categoryId)
        );
      }
      setMatches(categoryMatches);
    } catch (error) {
      logger.error('Error loading matches:', error);
      setMatches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMatches();
  }, [tournamentId, category?.id, category?.categoryId]);

  // Обработка drag and drop для перемещения участников
  const handleDragStart = (e: React.DragEvent, participant: Participant, matchId: number, position: 1 | 2) => {
    if (!isUserOrganizer || !canMoveParticipants) {
      e.preventDefault();
      return;
    }
    
    setDraggedParticipant({ participant, matchId, position });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ''); // Для совместимости
  };

  const handleDragOver = (e: React.DragEvent, matchId: number) => {
    if (!isUserOrganizer || !draggedParticipant || !canMoveParticipants) return;
    
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverMatch(matchId);
  };

  const handleDragLeave = () => {
    setDragOverMatch(null);
  };

  const handleDrop = async (e: React.DragEvent, targetMatch: ApiMatch, targetPosition: 1 | 2) => {
    if (!isUserOrganizer || !draggedParticipant || !targetMatch.id || !canMoveParticipants) {
      if (!canMoveParticipants) {
        message.warning('Перемещение участников возможно только до начала турнира');
      }
      return;
    }

    e.preventDefault();
    setDragOverMatch(null);

    // Не позволяем перемещать в тот же матч на ту же позицию
    if (draggedParticipant.matchId === targetMatch.id && draggedParticipant.position === targetPosition) {
      setDraggedParticipant(null);
      return;
    }

    try {
      setLoading(true);
      
      // Получаем ID участника
      const participantId = (draggedParticipant.participant as any).userId || 
                           (draggedParticipant.participant as any).id || 
                           (draggedParticipant.participant as any).participantId ||
                           (draggedParticipant.participant as any).user?.id;
      
      if (!participantId) {
        message.error('Не удалось определить участника');
        setDraggedParticipant(null);
        return;
      }

      // Проверяем, есть ли уже участник в целевой позиции (нужен обмен)
      const targetParticipant = targetPosition === 1 ? targetMatch.participant1 : targetMatch.participant2;
      const targetParticipantId = targetParticipant 
        ? ((targetParticipant as any).userId || (targetParticipant as any).id || (targetParticipant as any).participantId || (targetParticipant as any).user?.id)
        : null;

      // Обновляем целевой матч на сервере
      const updateData: any = {};
      if (targetPosition === 1) {
        updateData.participant1Id = participantId;
      } else {
        updateData.participant2Id = participantId;
      }

      await api.put(`/matches/${targetMatch.id}`, updateData);

      // Если участник был перемещен из другого матча
      if (draggedParticipant.matchId !== targetMatch.id) {
        const sourceMatch = matches.find(m => m.id === draggedParticipant.matchId);
        if (sourceMatch) {
          const sourceUpdateData: any = {};
          
          // Если в целевой позиции был участник, перемещаем его в исходное место
          if (targetParticipantId) {
            if (draggedParticipant.position === 1) {
              sourceUpdateData.participant1Id = targetParticipantId;
            } else {
              sourceUpdateData.participant2Id = targetParticipantId;
            }
          } else {
            // Если в целевой позиции не было участника, просто очищаем исходное место
            if (draggedParticipant.position === 1) {
              sourceUpdateData.participant1Id = null;
            } else {
              sourceUpdateData.participant2Id = null;
            }
          }
          
          await api.put(`/matches/${draggedParticipant.matchId}`, sourceUpdateData);
        }
      } else {
        // Если перемещение в том же матче (обмен позициями)
        if (targetParticipantId) {
          const swapUpdateData: any = {};
          if (targetPosition === 1) {
            // Перемещаем участника из позиции 1 в позицию 2
            swapUpdateData.participant2Id = targetParticipantId;
          } else {
            // Перемещаем участника из позиции 2 в позицию 1
            swapUpdateData.participant1Id = targetParticipantId;
          }
          await api.put(`/matches/${targetMatch.id}`, swapUpdateData);
        }
      }

      message.success('Участник перемещен');
      await loadMatches(); // Перезагружаем матчи
    } catch (error: any) {
      logger.error('Error moving participant:', error);
      message.error(error?.response?.data?.message || 'Ошибка при перемещении участника');
    } finally {
      setLoading(false);
      setDraggedParticipant(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedParticipant(null);
    setDragOverMatch(null);
  };

  // Экспорт в HTML для проверки (бэкенд рендерит сетку как в браузере).
  // Передаём только tournament_categories.id (category.id) — тот же id, по которому загружаются матчи (params.categoryId).
  const exportToHtml = async () => {
    if (matches.length === 0) {
      message.error('Нет данных для экспорта');
      return;
    }
    const categoryIdForExport = category?.id;
    if (categoryIdForExport == null) {
      message.error('Не удалось определить категорию для экспорта');
      return;
    }
    const participantsForLog = participants.map((p: any) => {
      const id = p?.id ?? p?.userId ?? p?.participantId;
      const lastName = p?.lastName ?? p?.last_name ?? '';
      const firstName = p?.firstName ?? p?.first_name ?? '';
      const fio = `${lastName} ${firstName}`.trim() || '—';
      const participantCategoryId =
        p?.categoryId ??
        (p as any).category_id ??
        (p as any).categoryId ??
        (p?.participant != null ? (p.participant as any).categoryId : undefined) ??
        categoryIdForExport ??
        null;
      return { id, fio, participantCategoryId };
    });
    const payload = {
      tournamentId,
      categoryIds: [categoryIdForExport],
      format: 'html' as const,
      matchesCount: matches.length,
      participantsCount: participants.length,
      category_tournament_categories_id: categoryIdForExport,
      category_categories_table_id: category?.categoryId ?? (category as any).categoryId,
      participants: participantsForLog,
    };
    logger.info('[HTML export] Запрос:', payload);
    try {
      setExporting(true);
      message.loading({ content: 'Получение HTML...', key: 'html-export' });
      const result = await TournamentService.exportBrackets(tournamentId, {
        categoryIds: [categoryIdForExport],
        format: 'html',
      });
      logger.info('[HTML export] Ответ:', {
        hasHtml: typeof result === 'object' && 'html' in result && !!result.html,
        htmlLength: typeof result === 'object' && result && 'html' in result ? String((result as { html: string }).html?.length ?? 0) : '—',
      });
      if (typeof result === 'object' && 'html' in result && result.html) {
        const w = window.open('', '_blank');
        if (w) {
          w.document.write(result.html);
          w.document.close();
        } else {
          message.warning('Разрешите всплывающие окна для просмотра HTML');
        }
      }
    } catch (e) {
      logger.error('HTML export failed', e);
      message.error({ content: 'Не удалось получить HTML', key: 'html-export' });
    } finally {
      setExporting(false);
      message.destroy('html-export');
    }
  };

  // Экспорт в PDF через тот же серверный рендер. Используем tournament_categories.id (category.id), как и для матчей.
  const exportToPDF = async () => {
    if (matches.length === 0) {
      message.error('Нет данных для экспорта');
      return;
    }
    const categoryIdForExport = category?.id;
    if (categoryIdForExport == null) {
      message.error('Категория не выбрана или не определена');
      return;
    }
    try {
      setExporting(true);
      message.loading({ content: 'Создание PDF...', key: 'pdf-export' });
      const fileName = `${category.name || 'tournament_bracket'}.pdf`;
      await TournamentService.downloadBracketsPdf(tournamentId, [categoryIdForExport], fileName);
      message.success({ content: 'PDF успешно создан!', key: 'pdf-export' });
    } catch (error) {
      logger.error('Error exporting to PDF:', error);
      message.error({ content: 'Ошибка при создании PDF', key: 'pdf-export' });
    } finally {
      setExporting(false);
    }
  };

  // Получение информации об участнике (match.participant1Id/2Id — это user id; приводим к числу для надёжного сравнения)
  const getParticipantInfo = (participantId?: number | null): Participant | null => {
    if (participantId == null) return null;
    const searchId = Number(participantId);
    if (Number.isNaN(searchId)) return null;

    const p = participants.find(p => {
      const pId = p.id ?? (p as any).userId ?? (p as any).participantId;
      return pId != null && Number(pId) === searchId;
    });

    return p ?? null;
  };

  // Получение имени участника
  const getParticipantName = (participant: Participant | null): string => {
    if (!participant) return '';
    const firstName = participant.firstName || (participant as any)?.first_name || '';
    const lastName = participant.lastName || (participant as any)?.last_name || '';
    return `${lastName} ${firstName}`.trim().toUpperCase() || `Участник #${participant.id}`;
  };

  // Участник из матча: сначала из вложенных participant1/participant2 (API), иначе из списка participants
  const getParticipantForMatch = (match: ApiMatch, position: 1 | 2): Participant | null => {
    const fromApi = position === 1 ? (match as any).participant1 : (match as any).participant2;
    if (fromApi && (fromApi.firstName != null || fromApi.lastName != null)) {
      return {
        id: fromApi.id,
        firstName: fromApi.firstName ?? '',
        lastName: fromApi.lastName ?? '',
        middleName: fromApi.middleName ?? '',
      } as Participant;
    }
    const id = position === 1 ? match.participant1Id : match.participant2Id;
    return getParticipantInfo(id ?? undefined);
  };

  // Проверка на split-сетку
  const isSplitBracket = useMemo(() => {
    return matches.some(match => match.side === 'left' || match.side === 'right');
  }, [matches]);

  const buildRounds = (sourceMatches: ApiMatch[], sectionPrefix: string = 'main', overrideBracketSize?: number): IRoundProps[] => {
    if (sourceMatches.length === 0) return [];

    // Группируем матчи по раундам и позициям
    const matchesByRound: Record<number, ApiMatch[]> = {};
    const positionsByRound: Record<number, number> = {};

    sourceMatches.forEach(match => {
      const round = match.round || 1;
      if (!matchesByRound[round]) matchesByRound[round] = [];
      matchesByRound[round].push(match);
      positionsByRound[round] = Math.max(positionsByRound[round] || 0, match.position || 0);
    });

    const roundNumbers = Object.keys(matchesByRound).map(Number).sort((a, b) => a - b);
    const maxRound = Math.max(...roundNumbers);

    // Размер сетки: для сплита правая часть использует тот же bracketSize, что и левая (override),
    // иначе по max позиции 1 раунда — иначе разное кол-во слотов (напр. 5 vs 8) ломает связи «в свой бой»
    const maxPosRound1 = positionsByRound[1] || 0;
    const bracketSize = overrideBracketSize ?? (maxPosRound1 > 0 ? maxPosRound1 * 2 : 2 ** maxRound);

    // Определяем, является ли это частью сплит-сетки (левая или правая часть)
    const isSplitPart = sectionPrefix === 'left' || sectionPrefix === 'right';

    return roundNumbers.map(roundNumber => {
      // Во всех раундах (и слева, и справа): сортируем по position 1, 2, 3, ... сверху вниз
      const roundMatches = (matchesByRound[roundNumber] || []).slice().sort((a, b) =>
        (a.position || 0) - (b.position || 0)
      );
      
      // Создаем уникальный префикс для ключей в зависимости от части сетки
      // Используем sectionPrefix (main/left/right/final) вместо простого left/right
      const sidePrefix = sectionPrefix;
      const byPos = new Map<number, ApiMatch>();
      roundMatches.forEach(m => {
        if (m.position) byPos.set(m.position, m);
      });

      // Определяем название раунда
      // Для сплит-сетки (левая/правая части) удваиваем названия раундов
      let roundTitle: string = `Раунд ${roundNumber}`;
      if (roundNumber === maxRound) {
        // В последнем раунде части сплит-сетки - это полуфинал (не финал)
        if (isSplitPart) {
          roundTitle = 'Полуфинал';
        } else {
          roundTitle = 'Финал';
        }
      } else if (roundNumber === maxRound - 1) {
        if (isSplitPart) {
          roundTitle = '1/4 финала';
        } else {
          roundTitle = 'Полуфинал';
        }
      } else if (roundNumber === maxRound - 2) {
        if (isSplitPart) {
          roundTitle = '1/8 финала';
        } else {
          roundTitle = '1/4 финала';
        }
      } else if (roundNumber === maxRound - 3) {
        if (isSplitPart) {
          roundTitle = '1/16 финала';
        } else {
          roundTitle = '1/8 финала';
        }
      } else if (roundNumber === maxRound - 4) {
        if (isSplitPart) {
          roundTitle = '1/32 финала';
        } else {
          roundTitle = '1/16 финала';
        }
      }
      
      // Проверяем, есть ли в этом раунде финал и/или бой за 3 место (поддержка camelCase и snake_case с API)
      const hasThirdPlace = roundMatches.some(m => !!(m as any).isThirdPlaceMatch || (m as any).is_third_place_match);
      const hasFinal = roundMatches.some(m => !!(m as any).isFinalMatch || (m as any).is_final_match);
      
      if (hasThirdPlace && hasFinal) {
        roundTitle = 'Бой за 3 место / Финал';
      } else if (hasThirdPlace && !hasFinal) {
        roundTitle = 'Бой за 3 место';
      } else if (hasFinal && roundNumber === maxRound) {
        roundTitle = 'Финал';
      }

      // Важно: заполняем ВСЕ позиции (включая "пустые" под баи),
      // иначе @sportsgram/brackets не сможет построить все линии/связи.
      // В последнем раунде может быть: только финал (1 позиция) или бой за 3 место + финал (2 позиции)
      let computedCount = Math.max(1, Math.floor(bracketSize / (2 ** roundNumber)));
      
      if (roundNumber === maxRound) {
        if (hasThirdPlace && hasFinal) {
          computedCount = 2; // seeds[0] = бой за 3 место (сверху), seeds[1] = финал (снизу)
        } else if (hasFinal) {
          computedCount = 1; // Только финал
        } else if (hasThirdPlace) {
          computedCount = 1; // Только бой за 3 место (legacy)
        }
      } else if (hasThirdPlace && !hasFinal) {
        computedCount = 1; // Бой за 3 место в отдельном раунде (legacy)
      }
      const expectedCount = Math.max(computedCount, positionsByRound[roundNumber] || 0);

      // КРИТИЧНО: Библиотека @sportsgram/brackets соединяет матчи по порядку в массиве seeds,
      // а не по position. Поэтому порядок seeds должен соответствовать логике соединений:
      // seeds[0] и seeds[1] -> nextRound.seeds[0]
      // seeds[2] и seeds[3] -> nextRound.seeds[1]
      // и т.д.
      // 
      // Для правильных соединений нужно:
      // 1. Найти все реальные position матчей в текущем раунде
      // 2. Отсортировать их по position
      // 3. Создать seeds в порядке, который соответствует логике соединений библиотеки
      // 4. Заполнить пропуски виртуальными матчами

      // Сортируем матчи по позициям (бой за 3 место — 1, финал — 2)
      const sortedMatches = Array.from(byPos.entries()).sort(([posA], [posB]) => posA - posB);

      // Обычная сетка (sectionPrefix === 'main'): в последнем раунде порядок [бой за 3 место, финал]. Сплит не трогаем.
      const seeds: SeedWithCustomData[] = [];
      
      for (let i = 0; i < expectedCount; i++) {
        const position = i + 1;
        
        let match: ApiMatch | undefined;
        if (roundNumber === maxRound && (hasThirdPlace || hasFinal)) {
          if (sectionPrefix === 'main') {
            // Обычная сетка: всегда seeds[0] = бой за 3 место (сверху), seeds[1] = финал (снизу)
            const isThird = (m: ApiMatch) => !!(m as any).isThirdPlaceMatch || (m as any).is_third_place_match;
            const isFinal = (m: ApiMatch) => !!(m as any).isFinalMatch || (m as any).is_final_match;
            const thirdPlaceMatch = sortedMatches.find(([_, m]) => isThird(m))?.[1];
            const finalMatch = sortedMatches.find(([_, m]) => isFinal(m))?.[1];
            match = (position === 1 ? thirdPlaceMatch : finalMatch) ?? byPos.get(position);
          } else {
            // Сплит и др.: seeds[0] = финал, seeds[1] = бой за 3 место (линии полуфинал -> финал)
            if (position === 1) {
              match = byPos.get(2) ?? sortedMatches.find(([_, m]) => m.isFinalMatch)?.[1] ?? sortedMatches.find(([_, m]) => m.isThirdPlaceMatch)?.[1];
            } else if (position === 2) {
              match = byPos.get(1) ?? sortedMatches.find(([_, m]) => m.isThirdPlaceMatch)?.[1];
            }
          }
        } else if (hasThirdPlace && !hasFinal) {
          if (position === 1) {
            match = sortedMatches.find(([_, m]) => m.isThirdPlaceMatch)?.[1];
          }
        } else {
          match = byPos.get(position);
        }

        if (!match) {
          // Создаем виртуальный матч для пустой позиции (бай)
          const virtualMatch: CustomSeedData['match'] = {
            __isVirtual: true,
            categoryId: category?.categoryId || category?.id,
            round: roundNumber,
            position,
            status: 'pending',
          } as any;

          // Создаем уникальный ID для виртуального матча
          const virtualUniqueId = `virtual-${roundNumber}-${sidePrefix}-${position}`;
          // side для виртуальных в сплите: иначе коннекторы (::before/::after) рисуются по ltr и вертикаль между раундами обрывается
          const virtualSide = isSplitPart && (sectionPrefix === 'right' || sectionPrefix === 'left') ? sectionPrefix : null;

          seeds.push({
            id: virtualUniqueId,
            date: new Date().toLocaleDateString('ru-RU'),
            teams: [{ name: ' ' }, { name: ' ' }],
            match: virtualMatch,
            participant1: null,
            participant2: null,
            winner: null,
            participant1Score: null,
            participant2Score: null,
            status: 'pending',
            matchNumber: null,
            isFinalMatch: false,
            isThirdPlaceMatch: false,
            isAutoAdvance: false,
            side: virtualSide,
          } as SeedWithCustomData);
          continue;
        }

        const participant1 = getParticipantForMatch(match, 1);
        const participant2 = getParticipantForMatch(match, 2);
        const winner = getParticipantInfo(match.winnerParticipantId ?? undefined);

        const team1Name = participant1 ? getParticipantName(participant1) : 'Ждет соперника';
        const team2Name = participant2 ? getParticipantName(participant2) : 'Ждет соперника';

        // Создаем уникальный ID, включающий информацию о раунде, позиции и части сетки
        // Это предотвращает дублирование ключей, когда один матч может быть в разных частях сетки
        const uniqueId = match.id 
          ? `${match.id}-${roundNumber}-${sidePrefix}-${position}`
          : match.matchNumber 
          ? `${match.matchNumber}-${roundNumber}-${sidePrefix}-${position}`
          : `virtual-${roundNumber}-${sidePrefix}-${position}`;
        
        seeds.push({
          id: uniqueId,
          date: match.startTime ? new Date(match.startTime).toLocaleDateString('ru-RU') : new Date().toLocaleDateString('ru-RU'),
          teams: [{ name: team1Name }, { name: team2Name }],
          match: match as any,
          participant1,
          participant2,
          winner,
          participant1Score: match.participant1Score,
          participant2Score: match.participant2Score,
          status: match.status,
          matchNumber: match.matchNumber,
          isFinalMatch: match.isFinalMatch,
          isThirdPlaceMatch: match.isThirdPlaceMatch,
          isAutoAdvance: match.isAutoAdvance,
          side: match.side,
        } as SeedWithCustomData);
      }

      // Для сплит-сетки делаем title уникальным, добавляя префикс части и номер раунда
      // Это необходимо, чтобы React не выдавал предупреждения о дублирующихся ключах
      // Префикс будет удален при отображении в roundTitleComponent
      const uniqueTitle = isSplitPart 
        ? `${sectionPrefix === 'left' ? 'Л' : sectionPrefix === 'right' ? 'П' : ''}${roundNumber}-${roundTitle}`
        : roundTitle;
      
      return { title: uniqueTitle, seeds } as IRoundProps;
    });
  };

  // Создаем раунды для основной сетки
  // Финал в отдельном раунде (1 позиция), бой за 3 место в отдельном раунде
  const rounds = useMemo(() => {
    const allRounds = buildRounds(matches, 'main');
    
    // Находим бой за 3 место (после изменений в генераторе он в раунде maxRound + 1)
    const thirdPlaceMatch = matches.find(m => m.isThirdPlaceMatch);
    
    // Логирование поиска боя за 3 место убрано для уменьшения шума в консоли
    
    if (!thirdPlaceMatch) {
      return allRounds;
    }
    
    // Раунд с обоими матчами (бой за 3 место + финал) оставляем как есть — не разделяем.
    
    // Если бой за 3 место не найден ни в одном раунде (legacy), добавляем отдельный раунд
    const thirdPlaceAlreadyInRounds = allRounds.some(round =>
      round.seeds.some((seed: any) => (seed as SeedWithCustomData).isThirdPlaceMatch)
    );
    if (!thirdPlaceAlreadyInRounds) {
      const thirdPlaceRound = buildRounds([thirdPlaceMatch], 'main');
      if (thirdPlaceRound.length > 0 && thirdPlaceRound[0].seeds.length > 0) {
        allRounds.push({
          title: `Бой за 3 место`,
          seeds: thirdPlaceRound[0].seeds
        } as IRoundProps);
      }
    }
    
    // Финальная проверка: убеждаемся, что нет дубликатов раундов "Бой за 3 место"
    // Ищем все раунды, которые ДЕЙСТВИТЕЛЬНО содержат бой за 3 место (hasThirdPlace === true)
    const thirdPlaceRounds = allRounds
      .map((r, idx) => ({ 
        title: r.title, 
        index: idx,
        hasThirdPlace: r.seeds.some((seed: any) => (seed as SeedWithCustomData).isThirdPlaceMatch),
        hasFinal: r.seeds.some((seed: any) => (seed as SeedWithCustomData).isFinalMatch)
      }))
      .filter(({ hasThirdPlace, hasFinal }) => hasThirdPlace && !hasFinal);
    
    if (thirdPlaceRounds.length > 1) {
      logger.warn('Found duplicate third place rounds', {
        rounds: thirdPlaceRounds,
        allRoundsTitles: allRounds.map(r => r.title)
      });
      
      // Оставляем только первый раунд "Бой за 3 место" (без финала), остальные удаляем
      // Сортируем по индексу, чтобы оставить первый
      thirdPlaceRounds.sort((a, b) => a.index - b.index);
      
      // Удаляем все остальные дубликаты (в обратном порядке, чтобы индексы не сбились)
      for (let i = thirdPlaceRounds.length - 1; i > 0; i--) {
        const indexToRemove = thirdPlaceRounds[i].index;        
        allRounds.splice(indexToRemove, 1);
      }
    }
    
    // Также удаляем раунды с названием "Бой за 3 место", но без реальных матчей за 3 место
    // (это могут быть пустые раунды, созданные по ошибке)
    for (let i = allRounds.length - 1; i >= 0; i--) {
      const round = allRounds[i];
      const hasThirdPlace = round.seeds.some((seed: any) => (seed as SeedWithCustomData).isThirdPlaceMatch);
      const hasFinal = round.seeds.some((seed: any) => (seed as SeedWithCustomData).isFinalMatch);
      
      // Если раунд называется "Бой за 3 место", но не содержит реальных матчей за 3 место - удаляем
      if (round.title === 'Бой за 3 место' && !hasThirdPlace && !hasFinal) {
        // Логирование удаления пустого раунда за 3 место убрано
        allRounds.splice(i, 1);
      }
    }
    
    // Отдельная колонка для боя за 3 место: последний раунд с двумя матчами разбиваем на два раунда
    const lastRound = allRounds[allRounds.length - 1];
    if (lastRound && lastRound.seeds.length === 2) {
      const isThirdPlace = (s: any) => !!(s as SeedWithCustomData).isThirdPlaceMatch || (s as any).match?.is_third_place_match;
      const isFinalMatch = (s: any) => !!(s as SeedWithCustomData).isFinalMatch || (s as any).match?.is_final_match;
      const third = lastRound.seeds.find((s: any) => isThirdPlace(s));
      const final = lastRound.seeds.find((s: any) => isFinalMatch(s));
      if (third && final) {
        allRounds.pop();
        allRounds.push({ title: 'Финал', seeds: [final] } as IRoundProps);
        allRounds.push({ title: 'Бой за 3 место', seeds: [third] } as IRoundProps);
      }
    }
    
    return allRounds;
  }, [matches, participants, category?.id]);
  
  // Для сплит-сетки объединяем все раунды в один массив: левая часть + финал + правая часть
  // Используем useRef для предотвращения дублирования логов
  const splitLogKeyRef = useRef<string>('');
  
  const splitAllRounds = useMemo(() => {
    if (!isSplitBracket) return [];
    
    // Находим максимальный раунд для левой и правой частей
    const leftMatchesAll = matches.filter(m => m.side === 'left' && !m.isThirdPlaceMatch);
    const rightMatchesAll = matches.filter(m => m.side === 'right' && !m.isThirdPlaceMatch);
    const leftMaxRound = leftMatchesAll.length > 0 ? Math.max(...leftMatchesAll.map(m => m.round || 1)) : 0;
    const rightMaxRound = rightMatchesAll.length > 0 ? Math.max(...rightMatchesAll.map(m => m.round || 1)) : 0;
    
    // Создаем уникальный ключ для отслеживания изменений
    const currentLogKey = `${leftMatchesAll.length}-${rightMatchesAll.length}-${matches.length}`;
    const shouldLog = currentLogKey !== splitLogKeyRef.current;
    if (shouldLog) {
      splitLogKeyRef.current = currentLogKey;
    }
    
    // Левая и правая части сплита
    const leftMatches = matches.filter(m => m.side === 'left' && !m.isThirdPlaceMatch);
    const rightMatches = matches.filter(m => m.side === 'right' && !m.isThirdPlaceMatch);

    // Единый bracketSize для сплита: max(левая, правая) по раунду 1, чтобы обе части имели одинаковое кол-во слотов
    // Иначе у правой maxPosRound1=5 (10) vs левой 8 (16) — неверные пары и «не в свой бой»
    const leftMaxPos1 = leftMatches.length ? Math.max(0, ...leftMatches.filter(m => (m.round || 1) === 1).map(m => m.position || 0)) : 0;
    const rightMaxPos1 = rightMatches.length ? Math.max(0, ...rightMatches.filter(m => (m.round || 1) === 1).map(m => m.position || 0)) : 0;
    const maxPos1 = Math.max(leftMaxPos1, rightMaxPos1);
    const splitBracketSize = maxPos1 > 0 ? maxPos1 * 2 : undefined;

    const allLeftRounds = buildRounds(leftMatches, 'left', splitBracketSize);
    
    const finalMatches = matches.filter(m => 
      (!m.side || m.side === null || m.side === undefined) && 
      m.isFinalMatch && 
      !m.isThirdPlaceMatch
    );
    let finalRounds = buildRounds(finalMatches, 'final');
    const thirdPlaceMatch = matches.find(m =>
      (!m.side || m.side === null || m.side === undefined) &&
      m.isThirdPlaceMatch
    );
    if (finalRounds.length > 0 && finalRounds[0].seeds.length > 0 && thirdPlaceMatch) {
      const thirdPlaceRounds = buildRounds([thirdPlaceMatch], 'thirdPlace');
      if (thirdPlaceRounds.length > 0 && thirdPlaceRounds[0].seeds.length > 0) {
        const finalSeed = finalRounds[0].seeds[0];
        const thirdPlaceSeed = thirdPlaceRounds[0].seeds[0];
        // Бой за 3 место сверху (seeds[0]), финал снизу (seeds[1])
        finalRounds = [{
          ...finalRounds[0],
          seeds: [thirdPlaceSeed, finalSeed],
          title: finalRounds[0].title,
        }];
      }
    }

    const allRightRounds = buildRounds(rightMatches, 'right', splitBracketSize);
    const reversedRightRounds = [...allRightRounds].reverse();
    
    // Убеждаемся, что последний раунд левой части содержит только финал части (один seed)
    // Это необходимо для правильного соединения с общим финалом
    // Библиотека @sportsgram/brackets автоматически соединяет последний seed предыдущего раунда
    // с первым seed следующего раунда, если в предыдущем раунде нечетное количество seeds
    const processedLeftRounds = allLeftRounds.map((round, index) => {
      if (index === allLeftRounds.length - 1) {
        // В последнем раунде левой части должен быть только финал части
        // Находим seed с финалом части (максимальный раунд левой части)
        const finalSeed = round.seeds.find((seed: any) => {
          const match = (seed as SeedWithCustomData).match;
          return match && match.round === leftMaxRound && match.side === 'left';
        });
        
        if (finalSeed) {
          // Оставляем только финал части, чтобы библиотека правильно соединила его с общим финалом
          return { ...round, seeds: [finalSeed] };
        } else if (round.seeds.length > 0) {
          // Если не нашли финал части, берем последний seed (должен быть финалом части)
          return { ...round, seeds: [round.seeds[round.seeds.length - 1]] };
        }
      }
      return round;
    });
    
    // Убеждаемся, что первый раунд правой части (последний после reverse) содержит только финал части
    const processedRightRounds = reversedRightRounds.map((round, index) => {
      if (index === 0) {
        // В первом раунде правой части (который является последним после reverse) должен быть только финал части
        const finalSeed = round.seeds.find((seed: any) => {
          const match = (seed as SeedWithCustomData).match;
          return match && match.round === rightMaxRound && match.side === 'right';
        });
        
        if (finalSeed) {
          // Оставляем только финал части
          return { ...round, seeds: [finalSeed] };
        } else if (round.seeds.length > 0) {
          // Если не нашли финал части, берем первый seed (должен быть финалом части после reverse)
          return { ...round, seeds: [round.seeds[0]] };
        }
      }
      return round;
    });
    
    // Объединяем все раунды: левая часть + финал + правая часть
    // Библиотека @sportsgram/brackets автоматически соединяет seeds между раундами:
    // - seeds[0] и seeds[1] из раунда N -> seeds[0] в раунде N+1
    // - Если в раунде N нечетное количество seeds, последний seed соединяется с первым seed раунда N+1
    // Поэтому важно, чтобы последний раунд левой части содержал только один seed (финал части),
    // который автоматически соединится с финалом (первый seed следующего раунда)
    
    // Проверяем nextMatchNumber для отладки и валидации связей
    // Логируем только предупреждения, успешные связи не логируем для уменьшения шума
    if (processedLeftRounds.length > 0) {
      const lastLeftRound = processedLeftRounds[processedLeftRounds.length - 1];
      if (lastLeftRound.seeds.length > 0) {
        const lastLeftSeed = lastLeftRound.seeds[lastLeftRound.seeds.length - 1] as SeedWithCustomData;
        const lastLeftMatch = lastLeftSeed.match;
        const nextMatchNumber = (lastLeftMatch as any)?.nextMatchNumber;
        
        // Проверяем, что nextMatchNumber указывает на финал
        if (finalRounds.length > 0 && finalRounds[0].seeds.length > 0) {
          const finalSeed = finalRounds[0].seeds[0] as SeedWithCustomData;
          const finalMatch = finalSeed.match;
          const finalMatchNumber = finalMatch?.matchNumber;
          
          if (nextMatchNumber && finalMatchNumber && nextMatchNumber !== finalMatchNumber) {
            logger.warn(`[SPLIT] Несоответствие nextMatchNumber: финал левой части (бой ${lastLeftMatch?.matchNumber}) указывает на бой ${nextMatchNumber}, но финал имеет номер ${finalMatchNumber}`);
          } else if (!nextMatchNumber) {
            logger.warn(`[SPLIT] ⚠ nextMatchNumber не установлен для финала левой части (бой ${lastLeftMatch?.matchNumber})`);
          }
        }
      }
    }        
    
    const allRounds = [...processedLeftRounds, ...finalRounds, ...processedRightRounds];
    
    const hasFinalAndThird = finalRounds.length > 0 && finalRounds[0].seeds.length === 2 &&
      finalRounds[0].seeds.some((s: any) => (s as SeedWithCustomData).isThirdPlaceMatch);
    if (finalRounds.length > 0 && finalRounds[0].seeds.length > 1 && !hasFinalAndThird) {
      const finalRound = finalRounds[0];
      const mainFinalSeed = finalRound.seeds.find((seed: any) => {
        const match = (seed as SeedWithCustomData).match;
        return match && match.isFinalMatch && !match.side;
      }) || finalRound.seeds[0];
      if (mainFinalSeed) {
        finalRounds[0] = { ...finalRound, seeds: [mainFinalSeed] };
      }
    }
    
    return allRounds;
  }, [matches, participants, category?.id, isSplitBracket]);
  
  // Для обратной совместимости оставляем отдельные переменные (но они не используются в сплите)
  const leftRoundsData = useMemo(() => {
    if (!isSplitBracket) return { roundsWithoutFinal: [], finalRound: null };
    return { roundsWithoutFinal: [], finalRound: null };
  }, [isSplitBracket]);
  
  const rightRoundsData = useMemo(() => {
    if (!isSplitBracket) return { roundsWithoutFinal: [], finalRound: null };
    return { roundsWithoutFinal: [], finalRound: null };
  }, [isSplitBracket]);
  
  const finalRounds = useMemo(() => {
    if (!isSplitBracket) return [];
    return [];
  }, [isSplitBracket]);

  // Участники, реально попавшие в сетку (по матчам) — считаем до любых return, чтобы не нарушать порядок хуков
  const participantsInBracketIds = useMemo(() => {
    const ids = new Set<number>();
    matches.forEach((m: ApiMatch) => {
      if (m.participant1Id) ids.add(Number(m.participant1Id));
      if (m.participant2Id) ids.add(Number(m.participant2Id));
    });
    return ids;
  }, [matches]);
  const participantsInBracketCount = participantsInBracketIds.size;

  // При 3 участниках: без линий, одна строка, компактный вид
  const isThreeParticipants = participants.length === 3;

  // Эффект для синхронизации ширины всех seed в одной колонке (раунде)
  useEffect(() => {
    if (matches.length === 0 || !bracketRef.current) return;

    // При 3 участниках: скрываем все соединительные линии (круговая система — линии не нужны)
    const hideAllBracketLinesForThreeParticipants = () => {
      if (!bracketRef.current || !isThreeParticipants) return;
      const wrapper = bracketRef.current;
      wrapper.querySelectorAll('svg').forEach((el) => {
        const svg = el as unknown as HTMLElement;
        if (svg.style) {
          svg.style.setProperty('display', 'none', 'important');
          svg.style.setProperty('visibility', 'hidden', 'important');
          svg.style.setProperty('opacity', '0', 'important');
        }
      });
      wrapper.querySelectorAll('line, path, polyline').forEach((el) => {
        const line = el as unknown as HTMLElement;
        if (line.style) {
          line.style.setProperty('display', 'none', 'important');
          line.style.setProperty('visibility', 'hidden', 'important');
        }
      });
      // Однострочная раскладка: контейнер раундов в одну строку
      const bracketEl = wrapper.querySelector('[class*="bracket"]') as HTMLElement;
      if (bracketEl) {
        bracketEl.style.setProperty('display', 'flex', 'important');
        bracketEl.style.setProperty('flex-direction', 'row', 'important');
      }
      wrapper.querySelectorAll('[class*="round"]').forEach((el) => {
        const round = el as HTMLElement;
        round.style.setProperty('flex-direction', 'row', 'important');
        round.style.setProperty('min-width', 'auto', 'important');
      });
    };

    // Функция для скрытия линий только справа от последней колонки (финал/бой за 3 место).
    // Не скрываем линии внутри раунда финала — там рисуются входящие соединители от полуфиналов (Бой 76 → Бой 151 и т.д.).
    const hideFinalRoundLines = () => {
      if (!bracketRef.current) return;
      
      const standardBracket = bracketRef.current.querySelector('.standard-bracket');
      if (!standardBracket) return; // Сплиты не трогаем
      
      // Собираем все раунды, в которых есть финал или бой за 3 место (для определения правой границы)
      const finalRoundSeeds = standardBracket.querySelectorAll('[data-is-third-place="true"], [data-is-final="true"]');
      const lastRounds = new Set<HTMLElement>();
      finalRoundSeeds.forEach((seed) => {
        const round = (seed as HTMLElement).closest('[class*="round"]') as HTMLElement | null;
        if (round && round.closest('.standard-bracket')) lastRounds.add(round);
      });
      if (lastRounds.size === 0) return;
      
      // Помечаем раунды для CSS (при необходимости), но не скрываем линии внутри них —
      // иначе пропадают входящие соединители от предыдущего раунда к финалу.
      lastRounds.forEach((round) => round.setAttribute('data-last-round-no-lines', 'true'));
      
      // Скрываем SVG/линии только справа от самой правой колонки (финал)
      const rightmostRound = Array.from(lastRounds).reduce((right, r) => {
        const rRect = r.getBoundingClientRect();
        const rightRect = right.getBoundingClientRect();
        return rRect.right > rightRect.right ? r : right;
      }, Array.from(lastRounds)[0]);
      if (bracketRef.current && rightmostRound) {
        const lastRoundRect = rightmostRound.getBoundingClientRect();
        const allSvgsInBracket = bracketRef.current.querySelectorAll('svg');
        allSvgsInBracket.forEach((svg) => {
          const svgRect = (svg as any).getBoundingClientRect();
          
          // Если SVG находится справа от последнего раунда
          if (svgRect.left > lastRoundRect.right) {
            (svg as any).style.setProperty('display', 'none', 'important');
            (svg as any).style.setProperty('opacity', '0', 'important');
            (svg as any).style.setProperty('visibility', 'hidden', 'important');
            (svg as any).style.setProperty('width', '0', 'important');
            (svg as any).style.setProperty('height', '0', 'important');
            try {
              (svg as any).remove();
            } catch (e) {
              // Игнорируем ошибки
            }
          }
        });
        
        // Скрываем только линии, которые целиком справа от последней колонки (не трогаем соединительные линии между раундами)
        const allLinesInBracket = bracketRef.current.querySelectorAll('line, path, polyline');
        allLinesInBracket.forEach((line) => {
          const lineRect = (line as any).getBoundingClientRect();
          if (lineRect.left > lastRoundRect.right) {
            (line as any).style.setProperty('display', 'none', 'important');
            (line as any).style.setProperty('opacity', '0', 'important');
            (line as any).style.setProperty('visibility', 'hidden', 'important');
            try {
              (line as any).remove();
            } catch (e) {
              // ignore
            }
          }
        });
      }
    };

    // Функция для изменения display финального столбца сплита
    const fixFinalColumnDisplay = () => {
      if (!bracketRef.current || !isSplitBracket) return;
      
      const finalColumn = bracketRef.current.querySelector('.split-final-column');
      if (!finalColumn) return;
      
      // Находим все элементы bracket внутри финального столбца
      const bracketElements = finalColumn.querySelectorAll('[class*="bracket"]');
      bracketElements.forEach((bracketEl) => {
        const el = bracketEl as HTMLElement;
        el.style.setProperty('display', 'block', 'important');
        el.style.setProperty('flex-direction', 'unset', 'important');
        el.style.setProperty('flex', 'unset', 'important');
      });
      
      // Находим все элементы round внутри финального столбца
      const roundElements = finalColumn.querySelectorAll('[class*="round"]');
      roundElements.forEach((roundEl) => {
        const el = roundEl as HTMLElement;
        el.style.setProperty('display', 'block', 'important');
        el.style.setProperty('flex-direction', 'unset', 'important');
        el.style.setProperty('width', '100%', 'important');
      });
    };

    // Функция для изменения display последнего столбца левой части сплита
    const fixLeftLastColumnDisplay = () => {
      if (!bracketRef.current || !isSplitBracket) return;
      
      const leftPart = bracketRef.current.querySelector('.split-left-part');
      if (!leftPart) return;
      
      // Находим все bracket элементы в левой части
      const bracketElements = leftPart.querySelectorAll('[class*="bracket"]');
      bracketElements.forEach((bracketEl) => {
        const el = bracketEl as HTMLElement;
        // Принудительно устанавливаем display: block для всего bracket контейнера
        el.style.setProperty('display', 'block', 'important');
        el.style.setProperty('flex-direction', 'unset', 'important');
        el.style.setProperty('flex', 'unset', 'important');
        el.style.display = 'block';
        el.style.flexDirection = 'unset';
        el.style.flex = 'unset';
      });
      
      // Находим все раунды в левой части
      const roundElements = leftPart.querySelectorAll('[class*="round"]');
      if (roundElements.length === 0) return;
      
      // Последний раунд - это последний столбец
      const lastRound = roundElements[roundElements.length - 1] as HTMLElement;
      
      // Изменяем display для последнего раунда
      lastRound.style.setProperty('display', 'block', 'important');
      lastRound.style.setProperty('flex-direction', 'unset', 'important');
      lastRound.style.setProperty('flex', 'unset', 'important');
      lastRound.style.setProperty('width', '100%', 'important');
      lastRound.style.display = 'block';
      lastRound.style.flexDirection = 'unset';
      lastRound.style.flex = 'unset';
      lastRound.style.width = '100%';
    };

    // Функция для стилизации финального раунда в сплите: финал на одном уровне с финалами левой и правой сетки
    const styleFinalRoundSeeds = () => {
      if (!bracketRef.current || !isSplitBracket) return;
      
      const container = bracketRef.current.querySelector('.split-bracket-container');
      if (!container) return;
      
      const bracket = container.querySelector('[class*="bracket"]') as HTMLElement;
      if (!bracket) return;
      
      const roundEls = Array.from(bracket.children).filter((el): el is HTMLElement => el instanceof HTMLElement);
      const finalColumnIndex = roundEls.findIndex((el) => el.querySelector('[data-is-final="true"]'));
      if (finalColumnIndex < 0) return;
      
      const finalRoundEl = roundEls[finalColumnIndex];
      const finalSeed = finalRoundEl.querySelector('[data-is-final="true"]') as HTMLElement;
      if (!finalSeed) return;
      
      const listEl = finalSeed.parentElement as HTMLElement;
      if (!listEl) return;
      
      listEl.style.setProperty('display', 'grid', 'important');
      listEl.style.setProperty('grid-template-rows', '1fr auto 1fr', 'important');
      listEl.style.setProperty('flex-direction', 'unset', 'important');
      listEl.style.setProperty('flex-flow', 'unset', 'important');
      listEl.style.setProperty('align-items', 'stretch', 'important');
      listEl.style.setProperty('justify-content', 'start', 'important');
      listEl.style.setProperty('flex', '1 1 0%', 'important');
      listEl.style.setProperty('min-height', '0', 'important');
      
      finalSeed.style.setProperty('grid-row', '2', 'important');
      
      const thirdPlaceSeed = listEl.querySelector('[data-is-third-place="true"]') as HTMLElement;
      if (thirdPlaceSeed) {
        thirdPlaceSeed.style.setProperty('grid-row', '1', 'important');
        thirdPlaceSeed.style.setProperty('align-self', 'start', 'important');
        thirdPlaceSeed.style.setProperty('margin-bottom', '24px', 'important');
      }
      
      const virtualSeed = listEl.querySelector('[data-virtual="true"]') as HTMLElement;
      if (virtualSeed) {
        virtualSeed.style.setProperty('display', 'none', 'important');
      }
      
      finalRoundEl.style.setProperty('align-self', 'stretch', 'important');
      
      // Выравнивание финала по вертикали с полуфиналами левой и правой сетки
      const alignFinalWithSemiFinals = () => {
        const leftSemiFinalRound = finalColumnIndex > 0 ? roundEls[finalColumnIndex - 1] : null;
        const leftSemiFinalSeed = leftSemiFinalRound?.querySelector('[class*="seed"]:not([data-virtual="true"])') as HTMLElement | null;
        if (!leftSemiFinalSeed || !finalSeed) return;
        const bracketRect = bracket.getBoundingClientRect();
        const leftTop = leftSemiFinalSeed.getBoundingClientRect().top - bracketRect.top;
        const finalTop = finalSeed.getBoundingClientRect().top - bracketRect.top;
        const delta = leftTop - finalTop;
        if (delta > 0) {
          finalRoundEl.style.setProperty('padding-top', `${Math.round(delta)}px`, 'important');
          listEl.style.removeProperty('margin-top');
        } else {
          finalRoundEl.style.removeProperty('padding-top');
          listEl.style.setProperty('margin-top', `${Math.round(delta)}px`, 'important');
        }
      };
      alignFinalWithSemiFinals();
      requestAnimationFrame(() => alignFinalWithSemiFinals());
    };

    // Функция для зеркалирования линий в правой части split-сетки
    // Применяем зеркалирование ко всем SVG элементам, связанным с правой частью
    // ЗЕРКАЛИРУЕМ ПО ОСИ X (горизонтально) - scaleX(-1)
    let lastMirrorLogTime = 0;
    const MIRROR_LOG_INTERVAL = 2000; // Логируем детально только раз в 2 секунды
    
    const mirrorRightBracketLines = () => {
      if (!bracketRef.current || !isSplitBracket) {
        return;
      }
      
      const now = Date.now();
      const shouldLogDetailed = now - lastMirrorLogTime > MIRROR_LOG_INTERVAL;
      if (shouldLogDetailed) {
        lastMirrorLogTime = now;
      }
      
      // Находим все seed элементы правой части по data-side="right"
      const rightSeeds = bracketRef.current.querySelectorAll('[data-side="right"]');
      if (rightSeeds.length === 0) {
        if (shouldLogDetailed) {
          logger.warn('[MIRROR] Не найдено seed элементов правой части');
        }
        return;
      }
      
      
      // Находим раунды, содержащие правую часть
      // Пробуем разные способы поиска раундов
      const rightRounds = new Set<HTMLElement>();
      rightSeeds.forEach((seed) => {
        // Способ 1: через closest
        let roundElement = (seed as HTMLElement).closest('[class*="round"]') as HTMLElement;
        
        // Способ 2: через родителя
        if (!roundElement) {
          let parent = (seed as HTMLElement).parentElement;
          while (parent && !roundElement) {
            if (parent.className && parent.className.includes('round')) {
              roundElement = parent;
              break;
            }
            parent = parent.parentElement;
          }
        }
        
        // Способ 3: через bracket контейнер
        if (!roundElement) {
          const bracketContainer = (seed as HTMLElement).closest('[class*="bracket"]');
          if (bracketContainer) {
            // Ищем раунд, который содержит этот seed
            const allRounds = bracketContainer.querySelectorAll('[class*="round"]');
            allRounds.forEach((round) => {
              if ((round as HTMLElement).contains(seed as HTMLElement)) {
                roundElement = round as HTMLElement;
              }
            });
          }
        }
        
        if (roundElement) {
          rightRounds.add(roundElement);
        }
      });
      
      // Находим границы правой части для более точного определения
      const rightRoundsArray = Array.from(rightRounds);
      let rightPartLeft = Infinity;
      let rightPartRight = -Infinity;
      
      // Если раунды не найдены, используем seed элементы для определения границ
      if (rightRoundsArray.length === 0) {
        rightSeeds.forEach((seed) => {
          const rect = (seed as HTMLElement).getBoundingClientRect();
          rightPartLeft = Math.min(rightPartLeft, rect.left);
          rightPartRight = Math.max(rightPartRight, rect.right);
        });
      } else {
        rightRoundsArray.forEach((round) => {
          const rect = round.getBoundingClientRect();
          rightPartLeft = Math.min(rightPartLeft, rect.left);
          rightPartRight = Math.max(rightPartRight, rect.right);
        });
      }
      
      // Находим все SVG элементы
      // Сначала ищем в bracket контейнере
      let allSvgs: NodeListOf<SVGSVGElement> | SVGSVGElement[] = bracketRef.current.querySelectorAll('svg');
      
      // Если не найдены, ищем в дочерних контейнерах
      if (allSvgs.length === 0) {
        const splitContainer = bracketRef.current.querySelector('.split-bracket-container');
        if (splitContainer) {
          allSvgs = splitContainer.querySelectorAll('svg');
        }
      }
      
      // Если все еще не найдены, ищем по координатам в документе
      let svgsArray: SVGSVGElement[] = [];
      if (allSvgs.length === 0 && rightPartLeft !== Infinity && rightPartRight !== -Infinity) {
        const allSvgsInDocument = document.querySelectorAll('svg');
        
        allSvgsInDocument.forEach((svg) => {
          if (!(svg instanceof SVGSVGElement)) return;
          
          // Пропускаем SVG внутри seed элементов с data-side="right"
          const isInsideRightSeed = svg.closest('[data-side="right"]');
          if (isInsideRightSeed) return;
          
          const svgRect = svg.getBoundingClientRect();
          const svgCenterX = svgRect.left + svgRect.width / 2;
          
          // Если SVG находится в области правой части
          if (svgCenterX >= rightPartLeft - 200 && svgCenterX <= rightPartRight + 200) {
            svgsArray.push(svg);
          }
        });
      } else {
        // Преобразуем NodeList в массив
        svgsArray = Array.from(allSvgs);
      }
            
      let svgsMirrored = 0;
      let svgsSkipped = 0;
      let svgsInsideSeed = 0;
      let svgsAlreadyMirrored = 0;
      
      svgsArray.forEach((svg: SVGSVGElement, index: number) => {
        if (!(svg instanceof SVGSVGElement)) {
          svgsSkipped++;
          return;
        }
        
        // Пропускаем SVG внутри seed элементов (они не должны зеркалиться)
        // Проверяем через data-side, так как классы хешированы
        const isInsideRightSeed = svg.closest('[data-side="right"]');
        if (isInsideRightSeed) {
          svgsInsideSeed++;
          return;
        }
        
        // Проверяем, был ли уже зазеркален
        const alreadyMirrored = svg.classList.contains('right-bracket-mirrored');
        
        // Проверяем, связан ли этот SVG с правой частью
        const svgRect = svg.getBoundingClientRect();
        
        // Используем координаты для определения принадлежности к правой части
        // так как поиск раундов не работает из-за styled-components
        let shouldMirror = false;
        
        if (rightPartLeft !== Infinity && rightPartRight !== -Infinity) {
          const svgCenterX = svgRect.left + svgRect.width / 2;
          
          // Проверяем, находится ли SVG в горизонтальной области правой части
          // Расширяем границы для захвата линий между раундами
          if (svgCenterX >= rightPartLeft - 200 && svgCenterX <= rightPartRight + 200) {
            // Дополнительная проверка: SVG должен быть между или внутри правой части
            if (svgRect.right >= rightPartLeft - 100 && svgRect.left <= rightPartRight + 100) {
              shouldMirror = true;
            }
          }
        }
        
        if (shouldMirror) {
          if (alreadyMirrored) {
            svgsAlreadyMirrored++;
            return;
          }
          
          // Вычисляем центр правой части для правильного transform-origin
          const rightPartCenterX = rightPartLeft !== Infinity && rightPartRight !== -Infinity
            ? (rightPartLeft + rightPartRight) / 2
            : undefined;
          
          // Логируем только если трансформация не применилась
          applyMirrorTransform(svg, shouldLogDetailed && index < 3 ? index : -1, rightPartCenterX);
          svgsMirrored++;
        }
      });
      
      // Также находим и зеркалим все линии и пути напрямую, если они находятся в области правой части
      // Всегда ищем линии во всем документе и фильтруем по координатам
      // Это более надежный способ, так как библиотека может создавать линии в разных местах DOM
      const allLinesInDocument = document.querySelectorAll('line, path, polyline');
      let linesArray: Element[] = [];
      
      if (rightPartLeft !== Infinity && rightPartRight !== -Infinity) {
        allLinesInDocument.forEach((line) => {
          const lineRect = line.getBoundingClientRect();
          // Пропускаем невидимые линии
          if (lineRect.width === 0 && lineRect.height === 0) return;
          
          const lineCenterX = lineRect.left + lineRect.width / 2;
          const lineLeft = lineRect.left;
          const lineRight = lineRect.right;
          
          // Линия должна пересекаться с областью правой части или быть рядом с ней
          // Расширяем границы для захвата линий, которые могут быть между раундами
          const isInRightArea = (
            (lineCenterX >= rightPartLeft - 300 && lineCenterX <= rightPartRight + 300) ||
            (lineLeft <= rightPartRight + 300 && lineRight >= rightPartLeft - 300)
          );
          
          if (isInRightArea) {
            linesArray.push(line);
          }
        });
      }
      
      let linesProcessed = 0;
      let linesInRightArea = 0;
      let linesOutsideRightArea = 0;
      let linesInsideSeed = 0;
      const linesInfo: Array<{tagName: string, centerX: number, inArea: boolean, hasParentSvg: boolean, isInsideSeed: boolean}> = [];
      
      // Сдвиг влево для правой части: чтобы линия приходила в центр следующего боя (не в center-right)
      let translateXAdjustment = 0;
      if (rightSeeds.length > 0) {
        let sumWidth = 0;
        rightSeeds.forEach((s) => {
          const r = (s as HTMLElement).getBoundingClientRect();
          sumWidth += r.width;
        });
        const avgSeedWidth = sumWidth / rightSeeds.length;
        translateXAdjustment = Math.round(Math.min(55, Math.max(18, avgSeedWidth * 0.28)));
      } else {
        translateXAdjustment = 28;
      }
      
      linesArray.forEach((line, lineIndex) => {
        const lineRect = line.getBoundingClientRect();
        if (rightPartLeft !== Infinity && rightPartRight !== -Infinity) {
          const lineCenterX = lineRect.left + lineRect.width / 2;
          // Используем те же расширенные границы, что и при поиске
          const isInRightArea = (
            (lineCenterX >= rightPartLeft - 300 && lineCenterX <= rightPartRight + 300) ||
            (lineRect.left <= rightPartRight + 300 && lineRect.right >= rightPartLeft - 300)
          );
          
          // Собираем информацию о первых 10 линиях для диагностики
          if (shouldLogDetailed && lineIndex < 10) {
            const parentSvg = line.closest('svg');
            const isInsideRightSeed = parentSvg?.closest('[data-side="right"]');
            linesInfo.push({
              tagName: line.tagName,
              centerX: Math.round(lineCenterX),
              inArea: isInRightArea,
              hasParentSvg: !!parentSvg,
              isInsideSeed: !!isInsideRightSeed
            });
          }
          
          if (isInRightArea) {
            linesInRightArea++;
            // Применяем зеркалирование к родительскому SVG, если он есть
            const parentSvg = line.closest('svg');
            if (parentSvg && parentSvg instanceof SVGSVGElement) {
              const isInsideRightSeed = parentSvg.closest('[data-side="right"]');                          
              
              if (isInsideRightSeed) {
                linesInsideSeed++;
              } else {
                // Для правой части линии должны выходить из левой стороны seed блока
                // Находим ближайший seed блок правой части и используем его левую границу
                let transformOriginX: number | undefined = undefined;
                // ОТКЛЮЧАЕМ доп. сдвиг точки соединения.
                // Он смещал всю линию влево, из‑за чего она больше не выходила
                // из правого края карточки (между 1–2 и 2–3 раундами появлялся зазор).
                let useArrivalShift = false;
                
                // Ищем ближайший seed блок правой части
                // Расширяем область поиска по вертикали, так как seed может быть дальше
                const lineCenterY = lineRect.top + lineRect.height / 2;
                const lineLeft = lineRect.left;
                
                let nearestRightSeed: HTMLElement | null = null;
                let minDistance = Infinity;
                
                rightSeeds.forEach((seed) => {
                  const seedEl = seed as HTMLElement;
                  if (!seedEl || !seedEl.getBoundingClientRect) return;
                  
                  const seedRect = seedEl.getBoundingClientRect();
                  const seedCenterY = seedRect.top + seedRect.height / 2;
                  const seedRight = seedRect.right;
                  
                  // Расстояние от правой границы seed до левой границы линии
                  const horizontalDistance = lineLeft - seedRight;
                  const verticalDistance = Math.abs(seedCenterY - lineCenterY);
                  
                  // Seed должен быть слева от линии (horizontalDistance >= 0)
                  // Увеличиваем границы поиска: вертикально до 800px (линии могут быть далеко по вертикали),
                  // горизонтально до 1000px (seed может быть далеко слева)
                  if (horizontalDistance >= 0 && horizontalDistance < 1000 && verticalDistance < 800) {
                    // Комбинированное расстояние (приоритет горизонтальному расстоянию)
                    // Но также учитываем, что seed должен быть в той же области по вертикали
                    const combinedDistance = horizontalDistance * 1.5 + verticalDistance * 0.5;
                    if (combinedDistance < minDistance) {
                      minDistance = combinedDistance;
                      nearestRightSeed = seedEl;
                    }
                  }
                });
                
                if (shouldLogDetailed && lineIndex < 3) {
                  void Array.from(rightSeeds).slice(0, 5).map(seed => {
                    if (!(seed instanceof HTMLElement)) return null;
                    const seedRect = seed.getBoundingClientRect();
                    const seedCenterY = seedRect.top + seedRect.height / 2;
                    const seedRight = seedRect.right;
                    const horizontalDistance = lineLeft - seedRight;
                    const verticalDistance = Math.abs(seedCenterY - lineCenterY);
                    return {
                      left: Math.round(seedRect.left),
                      right: Math.round(seedRect.right),
                      top: Math.round(seedRect.top),
                      bottom: Math.round(seedRect.bottom),
                      horizontalDistance: Math.round(horizontalDistance),
                      verticalDistance: Math.round(verticalDistance),
                      подходит: horizontalDistance >= 0 && horizontalDistance < 1000 && verticalDistance < 800
                    };
                  }).filter(Boolean);
                }
                
                if (nearestRightSeed) {
                  const seedEl = nearestRightSeed as HTMLElement;
                  const seedRect = seedEl.getBoundingClientRect();
                  const svgRect = parentSvg.getBoundingClientRect();
                  
                  // Вычисляем смещение от левой границы SVG до левой границы seed блока
                  let offsetX = seedRect.left - svgRect.left;
                  
                  // Для правой части линии должны выходить из левой стороны seed блока
                  // Если seed находится слева от SVG (offsetX < 0), используем правую границу SVG
                  // как transform-origin, чтобы линии зеркалились относительно правой стороны
                  // и выходили влево (из левой стороны seed)
                  if (offsetX < 0) {
                    // Seed слева от SVG - используем правую границу SVG; не применяем arrival shift
                    useArrivalShift = false;
                    transformOriginX = svgRect.width;
                  } else if (offsetX > svgRect.width) {
                    // Seed справа от SVG - используем правую границу SVG
                    transformOriginX = svgRect.width;
                  } else {
                    // Seed внутри или на границе SVG - используем вычисленное смещение
                    transformOriginX = offsetX;
                  } 
                } else {
                  // Если seed не найден, пытаемся найти seed блоки по координатам
                  // Линии находятся справа от seed блоков, поэтому ищем seed слева от линии
                  // Пробуем найти seed блоки, которые могут быть слева от SVG
                  // Используем координаты линии для определения, где должен быть seed
                  // В правой части seed блоки находятся слева от линий
                  // Линии должны выходить из левой стороны seed, значит seed.left должен быть < line.left
                  
                  // Если не нашли seed, используем левую границу SVG (0) как transform-origin
                  // Это обеспечит зеркалирование относительно левой стороны SVG,
                  // чтобы линии выходили вправо (как должны выходить из левой стороны seed блока)
                  transformOriginX = 0; // Левая граница SVG 
                }
                
                const adjustment = useArrivalShift ? translateXAdjustment : undefined;
                if (!parentSvg.classList.contains('right-bracket-mirrored')) {
                  // SVG не помечен как зазеркаленный - применяем трансформацию
                  // adjustment: сдвиг влево, чтобы линия приходила в центр следующего боя (правая часть)
                  applyMirrorTransform(parentSvg, shouldLogDetailed && lineIndex < 3 ? lineIndex : -1, transformOriginX, adjustment);
                  linesProcessed++;
                } else {
                  // SVG помечен как зазеркаленный - проверяем, действительно ли трансформация применена
                  const computedTransform = window.getComputedStyle(parentSvg).transform;
                  const hasTransform = computedTransform && computedTransform !== 'none' && 
                    (computedTransform.includes('scaleX') || computedTransform.includes('matrix'));
                  
                  if (!hasTransform || transformOriginX !== undefined) {                    
                    // Удаляем класс, чтобы применить трансформацию заново с правильным transform-origin и adjustment
                    parentSvg.classList.remove('right-bracket-mirrored');
                    applyMirrorTransform(parentSvg, -1, transformOriginX, adjustment);
                    linesProcessed++;
                  }
                }
              }
            } else if (shouldLogDetailed && lineIndex < 3) {
              logger.warn(`[MIRROR] Линия ${lineIndex}: не найден родительский SVG`, {
                tagName: line.tagName,
                centerX: Math.round(lineCenterX)
              });
            }
          } else {
            linesOutsideRightArea++;
          }
        }
      });            
    };
    
    // Вспомогательная функция для применения зеркального преобразования
    // ЗЕРКАЛИРУЕМ ПО ОСИ X (горизонтально) - scaleX(-1)
    // translateXAdjustment: сдвиг влево после зеркалирования, чтобы линия приходила в центр следующего боя (правая часть)
    const applyMirrorTransform = (svg: SVGSVGElement, debugIndex: number = -1, rightPartCenterX?: number, translateXAdjustment?: number) => {
      // Логируем детально только если debugIndex >= 0 (первые несколько элементов)
      const shouldLog = debugIndex >= 0;
      const debugPrefix = shouldLog ? `[APPLY-MIRROR #${debugIndex}]` : '[APPLY-MIRROR]';
      
      // Проверяем, не был ли уже зазеркален (но при изменении параметров переприменяем)
      const alreadyMirrored = svg.classList.contains('right-bracket-mirrored');
      if (alreadyMirrored && translateXAdjustment === undefined) {
        if (shouldLog) {
          logger.debug(`${debugPrefix} Уже зазеркален, пропускаем`);
        }
        return;
      }
      if (alreadyMirrored) {
        svg.classList.remove('right-bracket-mirrored');
        svg.removeAttribute('data-mirrored');
      }
      
      // Принудительно применяем CSS transform через inline стили
      const currentStyle = svg.getAttribute('style') || '';
      
      // Удаляем старые transform, если есть
      const cleanedStyle = currentStyle
        .replace(/transform\s*:\s*[^;]+;?/gi, '')
        .replace(/transform-origin\s*:\s*[^;]+;?/gi, '')
        .replace(/^\s*;\s*|\s*;\s*$/g, '');
      
      // Получаем размеры и позицию SVG
      const svgRect = svg.getBoundingClientRect();
      const svgWidth = svgRect.width || svg.viewBox?.baseVal?.width || 0;
      const svgHeight = svgRect.height || svg.viewBox?.baseVal?.height || 0;

      // Используем переданную X координату как точку зеркалирования (относительно левой границы SVG)
      // Это может быть левая граница seed блока или левая граница правой части
      // Иначе используем центр самого SVG
      let transformOriginX: string;
      if (rightPartCenterX !== undefined) {
        // rightPartCenterX - это смещение от левой границы SVG до левой границы seed/правой части
        // Если значение отрицательное - это значит seed находится слева от SVG, используем 0
        // Если значение больше ширины SVG - ограничиваем шириной SVG
        if (rightPartCenterX < 0) {
          // Seed находится слева от SVG - используем левую границу SVG
          transformOriginX = '0px';
        } else if (rightPartCenterX > svgWidth) {
          // Seed находится справа от SVG - используем правую границу SVG
          transformOriginX = `${svgWidth}px`;
        } else {
          // Нормальное смещение внутри SVG
          transformOriginX = `${rightPartCenterX}px`;
        }
      } else {
        // Используем центр SVG как точку зеркалирования
        transformOriginX = svgWidth > 0 ? `${svgWidth / 2}px` : 'center';
      }
      
      const transformOrigin = svgHeight > 0 
        ? `${transformOriginX} ${svgHeight / 2}px`
        : `${transformOriginX} center`;
      
      // Добавляем зеркальное отображение ПО ОСИ X (scaleX(-1), НЕ scaleY(-1))
      // translateX(-adjustment): сдвиг влево, чтобы линия приходила в центр следующего боя (правая часть)
      let transformValue = 'scaleX(-1)';
      if (rightPartCenterX !== undefined && rightPartCenterX < 0) {
        // Seed слева от SVG - нужно сместить линии после зеркалирования
        const seedRight = rightPartCenterX;
        const translateX = Math.abs(seedRight) * 2;
        transformValue = `translateX(${translateX}px) scaleX(-1)`;
      } else if (translateXAdjustment !== undefined && translateXAdjustment > 0) {
        // Правая часть: сдвиг влево, чтобы точка прихода была в центре следующего боя
        transformValue = `translateX(-${translateXAdjustment}px) scaleX(-1)`;
      }
      
      const newStyle = cleanedStyle 
        ? `${cleanedStyle}; transform: ${transformValue} !important; transform-origin: ${transformOrigin} !important;`.trim()
        : `transform: ${transformValue} !important; transform-origin: ${transformOrigin} !important;`;
      
      svg.setAttribute('style', newStyle);
      
      // Также применяем через CSS свойство напрямую
      try {
        const svgStyle = (svg as any).style;
        if (svgStyle) {
          svgStyle.setProperty('transform', transformValue, 'important');
          svgStyle.setProperty('transform-origin', transformOrigin, 'important');
          svgStyle.transform = transformValue;
          svgStyle.transformOrigin = transformOrigin;
        }
      } catch (e) {
        if (shouldLog) {
          logger.error(`${debugPrefix} Ошибка при применении через style.setProperty:`, e);
        }
      }
      
      // Добавляем класс и атрибут для CSS селекторов
      svg.classList.add('right-bracket-mirrored');
      svg.setAttribute('data-mirrored', 'true');            
    };

    // Функция для синхронизации ширины seed в колонке
    const syncSeedWidths = () => {
      // Находим все раунды (колонки)
      const roundsElements = bracketRef.current?.querySelectorAll('[class*="round"]');
      if (!roundsElements || roundsElements.length === 0) return;

      roundsElements.forEach((round) => {
        const roundElement = round as HTMLElement;
        
        // Находим все seed в этом раунде (исключаем виртуальные с opacity: 0)
        const seeds = Array.from(round.querySelectorAll('[class*="seed"]')).filter(seed => {
          const seedEl = seed as HTMLElement;
          const computedStyle = window.getComputedStyle(seedEl);
          return computedStyle.opacity !== '0' && !seedEl.hasAttribute('data-virtual');
        });
        
        if (seeds.length === 0) return;

        // Находим максимальную ширину среди всех seed в раунде
        // Измеряем ширину каждого блока участника (имя + клуб) для точного выравнивания
        let maxWidth = 0;
        
        // Сначала измеряем все seed, чтобы найти максимальную ширину
        seeds.forEach((seed) => {
          const seedElement = seed as HTMLElement;
          
          // Сохраняем текущие стили
          const originalWidth = seedElement.style.width;
          const originalMaxWidth = seedElement.style.maxWidth;
          const originalMinWidth = seedElement.style.minWidth;
          const originalDisplay = seedElement.style.display;
          const originalFlex = seedElement.style.flex;
          
          // Временно убираем все ограничения для измерения реальной ширины содержимого
          seedElement.style.width = 'auto';
          seedElement.style.maxWidth = 'none';
          seedElement.style.minWidth = 'auto';
          seedElement.style.display = 'block';
          seedElement.style.flex = 'none';
          
          // Принудительно пересчитываем layout
          seedElement.offsetHeight; // Force reflow
          
          // Измеряем ширину содержимого, включая внутренние элементы
          const seedTeams = seedElement.querySelectorAll('[class*="seed-team"]');
          let contentWidth = 0;
          
          // Проверяем ширину каждого блока участника (seed-team)
          seedTeams.forEach((team) => {
            const teamEl = team as HTMLElement;
            
            // Сохраняем текущие стили блока участника
            const originalTeamWidth = teamEl.style.width;
            const originalTeamMaxWidth = teamEl.style.maxWidth;
            const originalTeamMinWidth = teamEl.style.minWidth;
            const originalTeamDisplay = teamEl.style.display;
            const originalTeamFlex = teamEl.style.flex;
            const originalTeamWhiteSpace = teamEl.style.whiteSpace;
            
            // Временно убираем ограничения для измерения
            teamEl.style.width = 'auto';
            teamEl.style.maxWidth = 'none';
            teamEl.style.minWidth = 'auto';
            teamEl.style.display = 'block';
            teamEl.style.flex = 'none';
            teamEl.style.whiteSpace = 'nowrap'; // Временно убираем перенос для измерения
            
            // Принудительно пересчитываем layout
            teamEl.offsetHeight; // Force reflow
            
            // Измеряем ширину блока участника используя getBoundingClientRect для точности
            const teamRect = teamEl.getBoundingClientRect();
            const teamWidth = teamRect.width;
            contentWidth = Math.max(contentWidth, teamWidth);
            
            // Также проверяем ширину текстовых элементов внутри блока участника
            const textElements = teamEl.querySelectorAll('div');
            textElements.forEach((textEl) => {
              const textElement = textEl as HTMLElement;
              const originalTextWidth = textElement.style.width;
              const originalTextMaxWidth = textElement.style.maxWidth;
              const originalTextWhiteSpace = textElement.style.whiteSpace;
              
              // Временно убираем ограничения для измерения полной ширины текста
              textElement.style.width = 'auto';
              textElement.style.maxWidth = 'none';
              textElement.style.whiteSpace = 'nowrap'; // Временно убираем перенос для измерения
              
              // Принудительно пересчитываем layout
              textElement.offsetHeight; // Force reflow
              
              // Используем getBoundingClientRect для более точного измерения
              const textRect = textElement.getBoundingClientRect();
              const textWidth = textRect.width;
              contentWidth = Math.max(contentWidth, textWidth);
              
              // Восстанавливаем стили текстового элемента
              textElement.style.width = originalTextWidth;
              textElement.style.maxWidth = originalTextMaxWidth;
              textElement.style.whiteSpace = originalTextWhiteSpace;
            });
            
            // Восстанавливаем стили блока участника
            teamEl.style.width = originalTeamWidth;
            teamEl.style.maxWidth = originalTeamMaxWidth;
            teamEl.style.minWidth = originalTeamMinWidth;
            teamEl.style.display = originalTeamDisplay;
            teamEl.style.flex = originalTeamFlex;
            teamEl.style.whiteSpace = originalTeamWhiteSpace;
          });
          
          // Также измеряем общую ширину seed элемента используя getBoundingClientRect
          const seedRect = seedElement.getBoundingClientRect();
          const seedWidth = seedRect.width;
          contentWidth = Math.max(contentWidth, seedWidth);
          
          // Проверяем также внутренние div с текстом напрямую в seed
          const textDivs = seedElement.querySelectorAll('div');
          textDivs.forEach((div) => {
            const divEl = div as HTMLElement;
            // Пропускаем, если это уже seed-team (уже проверили выше)
            if (divEl.closest('[class*="seed-team"]')) return;
            
            const originalDivWidth = divEl.style.width;
            const originalDivMaxWidth = divEl.style.maxWidth;
            const originalDivWhiteSpace = divEl.style.whiteSpace;
            divEl.style.width = 'auto';
            divEl.style.maxWidth = 'none';
            divEl.style.whiteSpace = 'nowrap'; // Временно убираем перенос для измерения
            divEl.offsetHeight; // Force reflow
            const divRect = divEl.getBoundingClientRect();
            const divWidth = divRect.width;
            if (divWidth > contentWidth) {
              contentWidth = divWidth;
            }
            // Восстанавливаем стили
            divEl.style.width = originalDivWidth;
            divEl.style.maxWidth = originalDivMaxWidth;
            divEl.style.whiteSpace = originalDivWhiteSpace;
          });
          
          if (contentWidth > maxWidth) {
            maxWidth = contentWidth;
          }
          
          // Восстанавливаем стили
          seedElement.style.width = originalWidth;
          seedElement.style.maxWidth = originalMaxWidth;
          seedElement.style.minWidth = originalMinWidth;
          seedElement.style.display = originalDisplay;
          seedElement.style.flex = originalFlex;
        });

        // Применяем максимальную ширину ко всем seed в раунде
        if (maxWidth > 0) {
          // Добавляем небольшой отступ для безопасности
          const finalWidth = Math.max(maxWidth + 16, 200); // Минимум 200px, отступ 16px
          
          // Устанавливаем CSS переменную для раунда
          roundElement.style.setProperty('--round-seed-width', `${finalWidth}px`);
          
          // Применяем одинаковую ширину ко ВСЕМ seed в раунде
          seeds.forEach((seed) => {
            const seedElement = seed as HTMLElement;
            
            // Удаляем все существующие inline стили ширины для чистоты
            seedElement.style.removeProperty('width');
            seedElement.style.removeProperty('min-width');
            seedElement.style.removeProperty('max-width');
            seedElement.style.removeProperty('flex');
            
            // Принудительно устанавливаем одинаковую ширину для всех seed
            // Используем множественные способы для максимальной надежности
            
            // 1. Устанавливаем через setProperty с !important
            seedElement.style.setProperty('width', `${finalWidth}px`, 'important');
            seedElement.style.setProperty('min-width', `${finalWidth}px`, 'important');
            seedElement.style.setProperty('max-width', `${finalWidth}px`, 'important');
            seedElement.style.setProperty('flex', '0 0 auto', 'important');
            seedElement.style.setProperty('box-sizing', 'border-box', 'important');
            
            // 2. Также устанавливаем через прямое свойство style для переопределения
            seedElement.style.width = `${finalWidth}px`;
            seedElement.style.minWidth = `${finalWidth}px`;
            seedElement.style.maxWidth = `${finalWidth}px`;
            seedElement.style.flex = '0 0 auto';
            seedElement.style.boxSizing = 'border-box';
            
            // 3. Устанавливаем через CSS переменную для дополнительной надежности
            seedElement.style.setProperty('--seed-width', `${finalWidth}px`);
            
            // 4. Устанавливаем через прямое изменение style.cssText для максимальной надежности
            // Это переопределит любые inline стили, установленные библиотекой
            const currentStyle = seedElement.style.cssText || '';
            // Удаляем старые значения width, min-width, max-width, flex
            const cleanedStyle = currentStyle
              .replace(/width\s*:\s*[^;]+;?/gi, '')
              .replace(/min-width\s*:\s*[^;]+;?/gi, '')
              .replace(/max-width\s*:\s*[^;]+;?/gi, '')
              .replace(/flex\s*:\s*[^;]+;?/gi, '');
            // Устанавливаем новые значения с !important через setProperty
            seedElement.style.cssText = cleanedStyle;
            seedElement.style.setProperty('width', `${finalWidth}px`, 'important');
            seedElement.style.setProperty('min-width', `${finalWidth}px`, 'important');
            seedElement.style.setProperty('max-width', `${finalWidth}px`, 'important');
            seedElement.style.setProperty('flex', '0 0 auto', 'important');
            
            // Устанавливаем атрибут для CSS селектора
            seedElement.setAttribute('data-synced-width', `${finalWidth}`);
            
            // Также устанавливаем для внутренних seed-team элементов
            const seedTeams = seedElement.querySelectorAll('[class*="seed-team"]');
            seedTeams.forEach((team) => {
              const teamEl = team as HTMLElement;
              teamEl.style.setProperty('width', '100%', 'important');
              teamEl.style.setProperty('min-width', '100%', 'important');
              teamEl.style.setProperty('max-width', '100%', 'important');
              teamEl.style.setProperty('box-sizing', 'border-box', 'important');
            });
          });
          
          // Также устанавливаем ширину колонки (раунда) для адаптации
          roundElement.style.setProperty('width', 'auto', 'important');
          roundElement.style.setProperty('min-width', `${finalWidth}px`, 'important');
          roundElement.style.setProperty('max-width', 'none', 'important');
          roundElement.style.setProperty('flex-shrink', '0', 'important');
          roundElement.style.setProperty('flex-grow', '0', 'important');
          roundElement.style.setProperty('box-sizing', 'border-box', 'important');
          
          // Дополнительная проверка: убеждаемся, что все seed имеют одинаковую ширину
          // Выполняем несколько раз для надежности
          const verifyWidth = () => {
            seeds.forEach((seed) => {
              const seedElement = seed as HTMLElement;
              const computedWidth = parseFloat(window.getComputedStyle(seedElement).width);
              const expectedWidth = finalWidth;
              
              // Если ширина не совпадает (с допуском 2px), принудительно устанавливаем снова
              if (Math.abs(computedWidth - expectedWidth) > 2) {
                // Используем все способы для максимальной надежности
                seedElement.style.setProperty('width', `${finalWidth}px`, 'important');
                seedElement.style.setProperty('min-width', `${finalWidth}px`, 'important');
                seedElement.style.setProperty('max-width', `${finalWidth}px`, 'important');
                seedElement.style.setProperty('flex', '0 0 auto', 'important');
                seedElement.style.width = `${finalWidth}px`;
                seedElement.style.minWidth = `${finalWidth}px`;
                seedElement.style.maxWidth = `${finalWidth}px`;
                seedElement.style.flex = '0 0 auto';
              }
            });
          };
          
          // Выполняем проверку несколько раз с задержками
          requestAnimationFrame(() => {
            verifyWidth();
            hideFinalRoundLines(); // Скрываем линии при каждой проверке
            setTimeout(() => {
              verifyWidth();
              hideFinalRoundLines();
            }, 50);
            setTimeout(() => {
              verifyWidth();
              hideFinalRoundLines();
            }, 150);
            setTimeout(() => {
              verifyWidth();
              hideFinalRoundLines();
            }, 300);
            setTimeout(() => {
              verifyWidth();
              hideFinalRoundLines();
            }, 600);
            setTimeout(() => {
              verifyWidth();
              hideFinalRoundLines();
            }, 1000);
          });
          
          // Также устанавливаем постоянный мониторинг через MutationObserver для каждого seed
          // Это нужно для отслеживания изменений стилей библиотекой
          seeds.forEach((seed) => {
            const seedElement = seed as HTMLElement;
            const observer = new MutationObserver(() => {
              const computedWidth = parseFloat(window.getComputedStyle(seedElement).width);
              const expectedWidth = finalWidth;
              
              // Если ширина изменилась, восстанавливаем её
              if (Math.abs(computedWidth - expectedWidth) > 2) {
                seedElement.style.setProperty('width', `${finalWidth}px`, 'important');
                seedElement.style.setProperty('min-width', `${finalWidth}px`, 'important');
                seedElement.style.setProperty('max-width', `${finalWidth}px`, 'important');
                seedElement.style.setProperty('flex', '0 0 auto', 'important');
              }
              
              // Также скрываем линии при каждом изменении
              hideFinalRoundLines();
            });
            
            observer.observe(seedElement, {
              attributes: true,
              attributeFilter: ['style', 'class'],
              childList: true, // Отслеживаем добавление дочерних элементов (SVG может добавляться динамически)
              subtree: true // Отслеживаем изменения во всех дочерних элементах
            });
            
            // При изменении seed элемента также зеркалим линии
            setTimeout(() => mirrorRightBracketLines(), 50);
            
            // Сохраняем observer для последующей очистки
            widthObservers.push(observer);
            
            // Останавливаем наблюдение через 10 секунд
            const timeout = setTimeout(() => {
              observer.disconnect();
              const index = widthObservers.indexOf(observer);
              if (index > -1) {
                widthObservers.splice(index, 1);
              }
            }, 10000);
            widthObserverTimeouts.push(timeout);
          });
        }
      });
    };

    // Выполняем синхронизацию после рендеринга с несколькими попытками
    let attemptCount = 0;
    const maxAttempts = 10; // Увеличиваем количество попыток
    
    const trySync = () => {
      attemptCount++;
      hideAllBracketLinesForThreeParticipants();
      syncSeedWidths();
      hideFinalRoundLines();
      mirrorRightBracketLines();
      fixFinalColumnDisplay();
      fixLeftLastColumnDisplay();
      styleFinalRoundSeeds(); // Всегда 3 строки в финале (не зависит от split-final-column)
      
      // Если еще не достигли максимума попыток, пробуем еще раз
      if (attemptCount < maxAttempts) {
        // Первые попытки чаще, затем реже
        const delay = attemptCount <= 4 ? 50 : attemptCount <= 7 ? 150 : 300;
        setTimeout(trySync, delay);
      }
    };
    
    // Первая попытка сразу, затем через небольшие интервалы
    hideAllBracketLinesForThreeParticipants();
    const timeoutId1 = setTimeout(() => trySync(), 50);
    
    // Также выполняем синхронизацию после полной загрузки DOM
    const timeoutId2 = setTimeout(() => {
      attemptCount = 0;
      trySync();
    }, 500);
    
    // Постоянный интервал для скрытия/удаления линий и зеркалирования (на случай, если библиотека добавляет их динамически)
    const hideLinesInterval = setInterval(() => {
      hideAllBracketLinesForThreeParticipants();
      hideFinalRoundLines();
      mirrorRightBracketLines();
      fixFinalColumnDisplay();
      fixLeftLastColumnDisplay();
      styleFinalRoundSeeds();
      setTimeout(() => { mirrorRightBracketLines(); fixFinalColumnDisplay(); fixLeftLastColumnDisplay(); styleFinalRoundSeeds(); }, 10);
      setTimeout(() => { mirrorRightBracketLines(); fixFinalColumnDisplay(); fixLeftLastColumnDisplay(); styleFinalRoundSeeds(); }, 50);
    }, 50); // Каждые 50мс проверяем и удаляем линии (очень часто для максимальной надежности)
    
    // Также выполняем при изменении размера окна
    const handleResize = () => {
      attemptCount = 0;
      trySync();
    };
    window.addEventListener('resize', handleResize);
    
    // Используем ResizeObserver для отслеживания изменений содержимого
    // Наблюдаем за контейнером и за каждым seed элементом отдельно
    let resizeObserver: ResizeObserver | null = null;
    if (window.ResizeObserver && bracketRef.current) {
      resizeObserver = new ResizeObserver(() => {
        // При изменении размера любого элемента пересчитываем ширину всех блоков в раунде
        attemptCount = 0;
        // Используем requestAnimationFrame для более плавной синхронизации
        requestAnimationFrame(() => {
          hideFinalRoundLines();
          mirrorRightBracketLines();
          fixFinalColumnDisplay();
          fixLeftLastColumnDisplay();
          styleFinalRoundSeeds();
          setTimeout(() => {
            trySync();
            hideFinalRoundLines();
            mirrorRightBracketLines();
            fixFinalColumnDisplay();
            fixLeftLastColumnDisplay();
            styleFinalRoundSeeds();
          }, 50);
          setTimeout(() => {
            hideFinalRoundLines();
            mirrorRightBracketLines();
            fixFinalColumnDisplay();
            fixLeftLastColumnDisplay();
            styleFinalRoundSeeds();
          }, 100);
          setTimeout(() => {
            hideFinalRoundLines();
            mirrorRightBracketLines();
            fixFinalColumnDisplay();
            fixLeftLastColumnDisplay();
            styleFinalRoundSeeds();
          }, 200);
        });
      });
      
      // Наблюдаем за контейнером
      resizeObserver.observe(bracketRef.current);
      
      // Также наблюдаем за каждым seed элементом для более точного отслеживания
      const allSeeds = bracketRef.current.querySelectorAll('[class*="seed"]');
      allSeeds.forEach((seed) => {
        resizeObserver?.observe(seed as HTMLElement);
      });
    }
    
    // Используем MutationObserver для отслеживания изменений в содержимом
    let mutationObserver: MutationObserver | null = null;
    const widthObservers: MutationObserver[] = []; // Массив для хранения всех observers ширины
    const widthObserverTimeouts: NodeJS.Timeout[] = []; // Массив для хранения таймаутов очистки observers
    
    if (window.MutationObserver && bracketRef.current) {
      mutationObserver = new MutationObserver(() => {
        // При изменении содержимого пересчитываем ширину
        attemptCount = 0;
        // Используем requestAnimationFrame для более плавной синхронизации
        requestAnimationFrame(() => {
          hideFinalRoundLines();
          mirrorRightBracketLines();
          fixFinalColumnDisplay();
          fixLeftLastColumnDisplay();
          styleFinalRoundSeeds();
          setTimeout(() => {
            trySync();
            hideFinalRoundLines();
            mirrorRightBracketLines();
            fixFinalColumnDisplay();
            fixLeftLastColumnDisplay();
            styleFinalRoundSeeds();
          }, 100);
          setTimeout(() => {
            hideFinalRoundLines();
            mirrorRightBracketLines();
            fixFinalColumnDisplay();
            fixLeftLastColumnDisplay();
            styleFinalRoundSeeds();
          }, 200);
          setTimeout(() => {
            hideFinalRoundLines();
            mirrorRightBracketLines();
            fixFinalColumnDisplay();
            fixLeftLastColumnDisplay();
            styleFinalRoundSeeds();
          }, 300);
        });
      });
      
      mutationObserver.observe(bracketRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
        characterData: true
      });
      
      // Создаем отдельный MutationObserver специально для SVG элементов в правой части
      if (isSplitBracket && bracketRef.current) {
        const svgObserver = new MutationObserver(() => {
          // При любых изменениях в правой части немедленно применяем зеркалирование
          // Используем requestAnimationFrame для применения после рендеринга
          requestAnimationFrame(() => {
            mirrorRightBracketLines();
            // Дополнительные вызовы для надежности
            setTimeout(() => mirrorRightBracketLines(), 0);
            setTimeout(() => mirrorRightBracketLines(), 10);
            setTimeout(() => mirrorRightBracketLines(), 50);
          });
        });
        
        svgObserver.observe(bracketRef.current, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class', 'viewBox', 'width', 'height', 'data-side']
        });
        
        // Сохраняем observer для очистки
        setTimeout(() => svgObserver.disconnect(), 60000);
      }
      
      // При любых изменениях в DOM также зеркалим линии
      // Используем очень частый интервал для максимальной надежности
      const mirrorInterval = setInterval(() => {
        if (isSplitBracket) {
          requestAnimationFrame(() => {
            mirrorRightBracketLines();
          });
        }
      }, 16); // Каждые 16мс (примерно 60fps) проверяем и зеркалим линии
      
      // Очищаем интервал при размонтировании
      setTimeout(() => clearInterval(mirrorInterval), 60000); // Останавливаем через 60 секунд
    }

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      if (hideLinesInterval) {
        clearInterval(hideLinesInterval);
      }
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
      // Отключаем все observers ширины
      widthObservers.forEach(observer => observer.disconnect());
      widthObservers.length = 0;
      // Очищаем все таймауты
      widthObserverTimeouts.forEach(timeout => clearTimeout(timeout));
      widthObserverTimeouts.length = 0;
    };
  }, [matches, rounds, leftRoundsData, rightRoundsData, finalRounds, participants, isSplitBracket, isThreeParticipants]);

  // Кастомный компонент для рендеринга seed
  const CustomSeed = (props: IRenderSeedProps): React.ReactElement => {
    const { seed } = props;
    const customSeed = seed as SeedWithCustomData;
    const { participant1, participant2, winner, participant1Score, participant2Score, status, matchNumber, isFinalMatch, isThirdPlaceMatch, isAutoAdvance, side } = customSeed;
    const isCompact = participants.length === 3;
    const matchRound = Number((customSeed.match as any)?.round || 0);
    const isVirtual = !!(customSeed.match as any)?.__isVirtual;

    // Плейсхолдеры для пустых позиций (баи/пустые матчи) — нужны для корректных линий,
    // но визуально их не показываем.
    if (isVirtual) {
      // Контент скрываем (opacity на SeedItem), но Seed оставляем видимым для ::before/::after —
      // иначе вертикальная соединительная линия между раундами обрывается
      return (
        <Seed
          style={{ fontSize: 12, minWidth: '180px', width: 'auto', pointerEvents: 'none' as any }}
          data-virtual="true"
          data-virtual-first-round={matchRound === 1 ? 'true' : undefined}
          data-side={customSeed.side || undefined}
        >
          <SeedItem style={{ opacity: 0, minHeight: '50px' }}>
            <div style={{ width: '100%' }}>
              <SeedTeam style={{ padding: '10px 12px', minHeight: '50px' }}>
                <div>&nbsp;</div>
              </SeedTeam>
              <SeedTeam style={{ padding: '10px 12px', minHeight: '50px', marginTop: '4px' }}>
                <div>&nbsp;</div>
              </SeedTeam>
            </div>
          </SeedItem>
        </Seed>
      );
    }
    
    // Безопасное получение имен команд
    const team1Name = seed.teams?.[0]?.name || 'Ждет соперника';
    const team2Name = seed.teams?.[1]?.name || 'Ждет соперника';

    const isCompleted = status === 'completed';
    const isP1Winner = winner && participant1 && (winner.id === participant1.id || (winner as any).userId === (participant1 as any).userId);
    const isP2Winner = winner && participant2 && (winner.id === participant2.id || (winner as any).userId === (participant2 as any).userId);
    
    const club1 = (participant1 as any)?.club || (participant1 as any)?.Club || {};
    const club1Name = club1?.name || participant1?.clubName || '';
    const city1 = participant1?.city || club1?.city || (participant1 as any)?.user?.city || '';
    const region1 = (participant1 as any)?.federation?.region || club1?.region || (participant1 as any)?.federation?.region || (participant1 as any)?.region || '';
    
    const club2 = (participant2 as any)?.club || (participant2 as any)?.Club || {};
    const club2Name = club2?.name || participant2?.clubName || '';
    const city2 = participant2?.city || club2?.city || (participant2 as any)?.user?.city || '';
    const region2 = (participant2 as any)?.federation?.region || club2?.region || (participant2 as any)?.federation?.region || (participant2 as any)?.region || '';

    // Получаем ID матча
    const matchId = customSeed.match.id || (customSeed.match as any).matchNumber || 0;
    
    // Проверка возможности drag для участников
    const isP1Draggable = isUserOrganizer && !isCompleted && !!participant1 && canMoveParticipants && matchId > 0;
    const isP2Draggable = isUserOrganizer && !isCompleted && !!participant2 && canMoveParticipants && matchId > 0;
    const isP1Dragging = draggedParticipant?.matchId === matchId && draggedParticipant?.position === 1;
    const isP2Dragging = draggedParticipant?.matchId === matchId && draggedParticipant?.position === 2;
    const isDragOver = dragOverMatch === matchId;
    const isFirstRoundByeSeed = matchRound === 1 && ((!!participant1 && !participant2) || (!participant1 && !!participant2));
    const shouldHideFirstRoundIncomingLines = !isSplitBracket && isFirstRoundByeSeed;

    return (
      <Seed 
        style={{ fontSize: isCompact ? 9 : 12, position: 'relative' }}
        data-is-final={isFinalMatch ? 'true' : undefined}
        data-is-third-place={isThirdPlaceMatch ? 'true' : undefined}
        data-side={side || undefined}
        data-match-number={matchNumber ?? undefined}
      >
        {shouldHideFirstRoundIncomingLines && (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              left: '-36px',
              top: '-10px',
              bottom: '-10px',
              width: '36px',
              background: '#ffffff',
              zIndex: 4,
              pointerEvents: 'none',
            }}
          />
        )}
        <SeedItem>
          <div style={{ width: '100%', position: 'relative', overflow: 'visible' }}>
            {/* Первый участник */}
            <SeedTeam 
              data-team-index="0"
              draggable={isP1Draggable}
              onDragStart={(e) => {
                if (participant1 && matchId > 0) {
                  handleDragStart(e, participant1, matchId, 1);
                }
              }}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => {
                if (matchId > 0) {
                  handleDragOver(e, matchId);
                }
              }}
              onDragLeave={handleDragLeave}
              onDrop={(e) => {
                if (matchId > 0) {
                  handleDrop(e, customSeed.match as ApiMatch, 1);
                }
              }}
              style={{ 
                color: isP1Winner ? '#52c41a' : isCompleted && !isP1Winner ? '#8c8c8c' : '#262626',
                fontWeight: isP1Winner ? '700' : '600',
                backgroundColor: isDragOver ? 'rgba(24, 144, 255, 0.2)' : 
                                 isP1Dragging ? 'rgba(24, 144, 255, 0.1)' :
                                 isP1Winner ? '#f6ffed' : isAutoAdvance && !participant2 ? '#f0f9ff' : '#ffffff',
                padding: isCompact ? '4px 6px' : '10px 12px',
                border: isDragOver ? '2px solid #1890ff' :
                        isP1Dragging ? '2px solid #40a9ff' :
                        isP1Winner ? '2px solid #52c41a' : isAutoAdvance && !participant2 ? '2px solid #1890ff' : '1px solid #e8e8e8',
                borderRadius: '4px',
                marginBottom: isCompact ? '1px' : '4px',
                marginTop: '0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: isP1Winner ? '0 2px 4px rgba(82, 196, 26, 0.2)' : '0 1px 2px rgba(0,0,0,0.05)',
                minHeight: isCompact ? '36px' : '60px',
                height: isCompact ? '36px' : '60px',
                position: 'relative',
                boxSizing: 'border-box',
                cursor: isP1Draggable ? 'grab' : 'default',
                transition: 'all 0.2s ease'
              }}
              onMouseDown={(e) => {
                if (isP1Draggable) {
                  (e.currentTarget as HTMLElement).style.cursor = 'grabbing';
                }
              }}
              onMouseUp={(e) => {
                if (isP1Draggable) {
                  (e.currentTarget as HTMLElement).style.cursor = 'grab';
                }
              }}
            >
              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <div style={{ 
                  whiteSpace: 'nowrap', 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis',
                  lineHeight: '1.5',
                fontWeight: '600',
                fontSize: isCompact ? '10px' : '14px'
                }}>{team1Name}</div>
                {(region1 || city1 || club1Name) && (
                  <div style={{ 
                    fontSize: isCompact ? '8px' : '10px', 
                    color: '#666', 
                    marginTop: '2px', 
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {[region1, city1, club1Name].filter(Boolean).join(', ')}
                  </div>
                )}
              </div>
              {participant1Score !== undefined && participant1Score !== null && (
                <div style={{ 
                  fontWeight: 'bold', 
                  fontSize: isCompact ? '10px' : '16px',
                  color: isP1Winner ? '#52c41a' : '#000',
                  marginLeft: isCompact ? '4px' : '8px'
                }}>
                  {participant1Score}
                </div>
              )}
            </SeedTeam>
            
            {/* Номер матча по центру карточки (между двумя участниками) */}
            {matchNumber && (
              <div style={{ 
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: isCompact ? '8px' : '10px',
                fontWeight: 'bold',
                color: isFinalMatch ? '#faad14' : isThirdPlaceMatch ? '#722ed1' : '#1890ff',
                background: '#ffffff',
                padding: '2px 6px',
                borderRadius: '4px',
                border: `1px solid ${isFinalMatch ? '#faad14' : isThirdPlaceMatch ? '#722ed1' : '#1890ff'}`,
                zIndex: 10,
                whiteSpace: 'nowrap',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}>
                Бой {matchNumber}
              </div>
            )}
            
            {/* Второй участник */}
            <SeedTeam 
              data-team-index="1"
              draggable={isP2Draggable}
              onDragStart={(e) => {
                if (participant2 && matchId > 0) {
                  handleDragStart(e, participant2, matchId, 2);
                }
              }}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => {
                if (matchId > 0) {
                  handleDragOver(e, matchId);
                }
              }}
              onDragLeave={handleDragLeave}
              onDrop={(e) => {
                if (matchId > 0) {
                  handleDrop(e, customSeed.match as ApiMatch, 2);
                }
              }}
              style={{ 
                color: isP2Winner ? '#52c41a' : isCompleted && !isP2Winner ? '#8c8c8c' : '#262626',
                fontWeight: isP2Winner ? '700' : '600',
                backgroundColor: isDragOver ? 'rgba(24, 144, 255, 0.2)' : 
                                 isP2Dragging ? 'rgba(24, 144, 255, 0.1)' :
                                 isP2Winner ? '#f6ffed' : isAutoAdvance && !participant1 ? '#f0f9ff' : '#ffffff',
                padding: isCompact ? '4px 6px' : '10px 12px',
                border: isDragOver ? '2px solid #1890ff' :
                        isP2Dragging ? '2px solid #40a9ff' :
                        isP2Winner ? '2px solid #52c41a' : isAutoAdvance && !participant1 ? '2px solid #1890ff' : '1px solid #e8e8e8',
                borderRadius: '4px',
                marginTop: '0',
                marginBottom: '0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: isP2Winner ? '0 2px 4px rgba(82, 196, 26, 0.2)' : '0 1px 2px rgba(0,0,0,0.05)',
                minHeight: isCompact ? '36px' : '60px',
                height: isCompact ? '36px' : '60px',
                position: 'relative',
                boxSizing: 'border-box',
                cursor: isP2Draggable ? 'grab' : 'default',
                transition: 'all 0.2s ease'
              }}
              onMouseDown={(e) => {
                if (isP2Draggable) {
                  (e.currentTarget as HTMLElement).style.cursor = 'grabbing';
                }
              }}
              onMouseUp={(e) => {
                if (isP2Draggable) {
                  (e.currentTarget as HTMLElement).style.cursor = 'grab';
                }
              }}
            >
              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <div style={{ 
                  whiteSpace: 'nowrap', 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis',
                  lineHeight: '1.5',
                fontWeight: '600',
                fontSize: isCompact ? '10px' : '14px'
                }}>{team2Name}</div>
                {(region2 || city2 || club2Name) && (
                  <div style={{ 
                    fontSize: isCompact ? '8px' : '10px', 
                    color: '#666', 
                    marginTop: '2px', 
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {[region2, city2, club2Name].filter(Boolean).join(', ')}
                  </div>
                )}
              </div>
              {participant2Score !== undefined && participant2Score !== null && (
                <div style={{ 
                  fontWeight: 'bold', 
                  fontSize: isCompact ? '10px' : '16px',
                  color: isP2Winner ? '#52c41a' : '#000',
                  marginLeft: isCompact ? '4px' : '8px'
                }}>
                  {participant2Score}
                </div>
              )}
            </SeedTeam>
          </div>
        </SeedItem>
      </Seed>
    );
  };

  // Кастомный компонент для заголовка раунда
  const roundTitleComponent = (title: React.ReactNode) => {
    let titleStr = String(title ?? '');
    
    // Убираем префиксы "Л" и "П" и номер раунда из названий раундов для отображения
    // (они нужны только для уникальности ключей React в сплит-сетке)
    // Формат: "Л3-1/4 финала" -> "1/4 финала"
    titleStr = titleStr.replace(/^[ЛП]\d+-/, '');
    
    const isFinalLike = titleStr.toLowerCase().includes('финал');
    const hasBothFinalAndThird = titleStr.toLowerCase().includes('финал') && titleStr.toLowerCase().includes('3 место');
    
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '12px',
        background: hasBothFinalAndThird ? 'linear-gradient(to bottom, #f6ffed, #fff7e6)' : '#ffffff',
        border: `2px solid ${isFinalLike ? '#52c41a' : '#1890ff'}`,
        borderRadius: '6px',
        fontWeight: 'bold',
        fontSize: '14px',
        color: isFinalLike ? '#52c41a' : '#1890ff',
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        position: 'relative'
      }}>
        {titleStr}
        {hasBothFinalAndThird && (
          <div style={{
            fontSize: '11px',
            color: '#8c8c8c',
            marginTop: '4px',
            fontWeight: 'normal',
            fontStyle: 'italic'
          }}>           
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="min-h-[400px]">
        <div className="flex flex-col items-center justify-center h-[400px]">
          <Spin size="large" />
          <Text className="mt-4 text-gray-600">Загрузка матчей...</Text>
        </div>
      </Card>
    );
  }

  if (!category) {
    return (
      <Card>
        <Empty description="Категория не выбрана" />
      </Card>
    );
  }

  if (matches.length === 0) {
    return (
      <Card>
        <Empty description="Нет матчей" />
      </Card>
    );
  }

  return (
    <Card className="classic-bracket-container" style={{ background: '#ffffff', overflow: 'visible', maxWidth: '100%', border: 'none' }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap="middle" className="mb-6">
        <Flex align="center" gap={12}>
          <TrophyOutlined className="text-2xl text-yellow-600" />
          <div>
            <Title level={3} className="mb-0" style={{ color: '#262626' }}>
              {category?.name || 'Категория'}
              {category?.categoryId && (
                <Tag color="blue" style={{ marginLeft: '8px', fontSize: '14px', fontWeight: 'normal' }}>
                  ID: {category.categoryId}
                </Tag>
              )}
            </Title>
            {category && (
              <div style={{ fontSize: '14px', color: '#8c8c8c', marginTop: '4px' }}>
                Участников: {participantsInBracketCount} | Матчей: {matches.length}
              </div>
            )}
          </div>
        </Flex>

        <Space size="middle" wrap>
          <Button icon={<ReloadOutlined />} onClick={loadMatches}>
            Обновить
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={exportToHtml}
            loading={exporting}
            disabled={matches.length === 0}
          >
            HTML
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={exportToPDF}
            loading={exporting}
            disabled={matches.length === 0}
          >
            Экспорт в PDF
          </Button>
        </Space>
      </Flex>

      <div 
        ref={bracketRef}
        className={`bracket-view-wrapper${participants.length === 3 ? ' bracket-no-connector-lines' : ''}${participants.length === 2 ? ' bracket-two-participants' : ''}`}
        style={{ 
          position: 'relative',
          padding: '20px 20px 56px 20px', 
          background: '#ffffff', 
          borderRadius: '8px',
          minHeight: '400px',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
          overflowY: 'visible',
          boxSizing: 'border-box',
          border: 'none'
        }}
      >
        <style>{`
          .classic-bracket-container.ant-card {
            border: none !important;
            box-shadow: none !important;
            overflow: visible !important;
          }
          .classic-bracket-container.ant-card .ant-card-body {
            overflow: visible !important;
          }
          .bracket-view-wrapper {
            border: none !important;
          }
          /* В 1-м раунде виртуальные seed нужны для геометрии, но от них не нужны линии. */
          .standard-bracket .sportsgram-bracket-light [data-virtual-first-round="true"]::before,
          .standard-bracket .sportsgram-bracket-light [data-virtual-first-round="true"]::after {
            content: none !important;
            display: none !important;
            border: none !important;
            width: 0 !important;
            height: 0 !important;
          }
          /* 2 участника: линии должны выходить из правой части слота участника */
          .bracket-view-wrapper.bracket-two-participants .sportsgram-bracket-light [class*="bracket"] {
            justify-content: flex-start !important;
          }
          .bracket-view-wrapper.bracket-two-participants .sportsgram-bracket-light [class*="bracket"] [class*="round"] {
            min-width: 220px !important;
            width: 220px !important;
            flex: 0 0 220px !important;
          }
          .bracket-view-wrapper.bracket-two-participants .sportsgram-bracket-light [class*="bracket"] [class*="round"] [class*="seed"] {
            width: 100% !important;
            min-width: 100% !important;
            box-sizing: border-box !important;
          }
          .bracket-view-wrapper.bracket-two-participants .sportsgram-bracket-light [class*="seed-team"] {
            width: 100% !important;
          }
          /* Коннектор (round-gap): выравниваем по правому краю слота — SVG рисуется от левого края gap */
          .bracket-view-wrapper.bracket-two-participants .sportsgram-bracket-light [class*="round-gap"] {
            flex: 0 0 auto !important;
            min-width: 60px !important;
          }
          /* Линии коннектора: стартуют от правого края слота и выходят вправо к номеру следующего боя */
          .bracket-view-wrapper.bracket-two-participants .sportsgram-bracket-light [class*="seed"]::after,
          .bracket-view-wrapper.bracket-two-participants .sportsgram-bracket-light [class*="seed"]::before {
            left: auto !important;
            right: 0 !important;
          }
          /* Горизонтальный сегмент (::before): выводим линию вправо за край слота к коннектору/номеру боя */
          .bracket-view-wrapper.bracket-two-participants .sportsgram-bracket-light [class*="seed"]::before {
            right: -1em !important;
            width: 2.5em !important;
          }
          /* 3 участника: полностью скрываем все соединительные линии (круговая система) */
          .bracket-view-wrapper.bracket-no-connector-lines svg,
          .bracket-view-wrapper.bracket-no-connector-lines line,
          .bracket-view-wrapper.bracket-no-connector-lines path,
          .bracket-view-wrapper.bracket-no-connector-lines polyline {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
            stroke: none !important;
            pointer-events: none !important;
          }
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light line,
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light path,
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light polyline,
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light svg,
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light svg line,
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light svg path,
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light svg polyline {
            display: none !important;
            visibility: hidden !important;
            stroke: none !important;
          }
          /* 3 участника: одна строка — все раунды в ряд */
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light,
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light [class*="bracket"] {
            display: flex !important;
            flex-direction: row !important;
            align-items: flex-start !important;
          }
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light [class*="round"] {
            flex-direction: row !important;
            min-width: auto !important;
            width: auto !important;
            display: flex !important;
          }
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light [class*="seed"] {
            min-width: 92px !important;
            max-width: 120px !important;
          }
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light [class*="seed-team"],
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light [class*="seed-team"] *,
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light [class*="seed-item"] {
            font-size: 10px !important;
          }
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light [class*="match-header"],
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light [class*="seed"] [class*="seed-team"] > div {
            font-size: 10px !important;
          }
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light {
            font-size: 10px !important;
          }
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light * {
            font-size: inherit !important;
          }
          .bracket-view-wrapper.bracket-no-connector-lines .sportsgram-bracket-light [class*="round-gap"] {
            width: 14px !important;
            min-width: 14px !important;
          }
          .sportsgram-bracket-light {
            background: #ffffff !important;
          }
          /* ВАЖНО: не задаем background всем элементам через '*',
             иначе коннекторы (svg) и горизонтальные сегменты оказываются "под" белыми блоками. */
          .sportsgram-bracket-light,
          .sportsgram-bracket-light * {
            color: #262626 !important;
          }
          
          /* Улучшаем качество рендеринга текста */
          .sportsgram-bracket-light {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
          }
          .sportsgram-bracket-light svg {
            background: transparent !important;
          }
          .sportsgram-bracket-light svg line {
            stroke: #1890ff !important;
            stroke-width: 2px !important;
            stroke-linecap: round !important;
            stroke-linejoin: round !important;
            shape-rendering: geometricPrecision !important;
            vector-effect: non-scaling-stroke !important;
          }
          
          /* Улучшаем качество рендеринга SVG */
          .sportsgram-bracket-light svg {
            shape-rendering: geometricPrecision !important;
            text-rendering: optimizeLegibility !important;
            image-rendering: -webkit-optimize-contrast !important;
            image-rendering: crisp-edges !important;
          }
          
          /* Улучшаем четкость блоков */
          .sportsgram-bracket-light [class*="seed-team"] {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
          }
          /* Контейнер bracket - используем flex для адаптивных колонок */
          .sportsgram-bracket-light [class*="bracket"] {
            display: flex !important;
            flex-direction: row !important;
            align-items: flex-start !important;
            gap: 0 !important;
            width: 100% !important;
            overflow-x: auto !important;
            overflow-y: visible !important;
            /* Позволяем колонкам адаптироваться под содержимое */
            justify-content: flex-start !important;
          }
          
          /* Для сплит-сетки - одна большая таблица со всеми столбцами */
          .split-bracket-container .sportsgram-bracket-light [class*="bracket"] {
            display: flex !important;
            flex-direction: row !important;
            gap: 0 !important;
          }
          
          /* Для финального столбца сплита изменяем display - используем более высокую специфичность */
          .split-final-column .sportsgram-bracket-light [class*="bracket"],
          .split-final-column .sportsgram-bracket-light [class*="bracket"] * {
            display: block !important;
            flex-direction: unset !important;
            flex: unset !important;
          }
          
          .split-final-column .sportsgram-bracket-light [class*="round"] {
            display: block !important;
            flex-direction: unset !important;
            width: 100% !important;
          }
          
          /* Для bracket контейнера левой части сплита изменяем display на block */
          .split-left-part .sportsgram-bracket-light [class*="bracket"] {
            display: block !important;
            flex-direction: unset !important;
            flex: unset !important;
          }
          
          /* Для всех раундов в левой части */
          .split-left-part .sportsgram-bracket-light [class*="round"] {
            display: block !important;
            flex-direction: unset !important;
            flex: unset !important;
            width: 100% !important;
          }
          
          /* Для последнего столбца левой части сплита - дополнительное правило */
          .split-left-part .sportsgram-bracket-light [class*="round"]:last-child,
          .split-left-part .sportsgram-bracket-light [class*="round"]:last-of-type {
            display: block !important;
            flex-direction: unset !important;
            flex: unset !important;
            width: 100% !important;
          }
          /* Зеркальное отображение для правой части сплит-сетки */
          /* В сплит-сетке правая часть находится в общем массиве раундов */
          /* Нужно найти раунды правой части и применить зеркалирование */
          .split-bracket-container .sportsgram-bracket-light [class*="bracket"] {
            /* Раунды правой части уже развернуты в массиве, но нужно зеркалить линии */
          }
          
          /* Для правой части в сплите применяем зеркалирование линий */
          /* Находим раунды правой части по содержимому (маты с side='right') */
          .split-bracket-container .sportsgram-bracket-light [class*="round"]:has([data-side="right"]) {
            /* Раунды правой части - применяем зеркалирование через JavaScript */
          }
          
          /* Зеркальное отображение для всех SVG элементов правой части в сплите */
          /* Применяем к SVG элементам, помеченным как зеркалированные */
          .split-bracket-container svg.right-bracket-mirrored,
          .split-bracket-container svg[data-mirrored="true"],
          .split-bracket-container .sportsgram-bracket-light svg.right-bracket-mirrored,
          .split-bracket-container .sportsgram-bracket-light svg[data-mirrored="true"] {
            transform: scaleX(-1) !important;
            -webkit-transform: scaleX(-1) !important;
            -moz-transform: scaleX(-1) !important;
            -ms-transform: scaleX(-1) !important;
            transform-origin: center !important;
            -webkit-transform-origin: center !important;
            -moz-transform-origin: center !important;
            -ms-transform-origin: center !important;
          }
          
          /* Важно: SVG внутри seed элементов (например, для автопродвижения) НЕ зеркалим */
          .split-bracket-container .sportsgram-bracket-light [class*="seed"] svg,
          .split-bracket-container .sportsgram-bracket-light [class*="seed-item"] svg,
          .split-bracket-container [class*="seed"] svg {
            transform: none !important;
          }
          /* Правая часть сплита: линии выходят из ЛЕВОЙ стороны блока, приходят в центр следующего */
          .split-bracket-container .sportsgram-bracket-light [data-side="right"]::after {
            right: auto !important;
            left: 0 !important;
          }
          /* Горизонтальный отрезок (::before) — тянется влево к вертикали, width 1.5em как в библиотеке */
          .split-bracket-container .sportsgram-bracket-light [data-side="right"]::before {
            right: auto !important;
            left: -1.5em !important;
            width: 1.5em !important;
          }
          .split-bracket-container .sportsgram-bracket-light [data-side="right"]:nth-child(even)::after,
          .split-bracket-container .sportsgram-bracket-light [data-side="right"]:nth-child(odd)::after {
            border-left: 1px solid #707070 !important;
            border-right: none !important;
          }
          /* Убираем артефактную вертикальную линию у финала и боя за 3 место: отключаем коннекторы */
          .split-bracket-container .sportsgram-bracket-light [data-is-final="true"]::before,
          .split-bracket-container .sportsgram-bracket-light [data-is-final="true"]::after,
          .split-bracket-container .sportsgram-bracket-light [data-is-third-place="true"]::before,
          .split-bracket-container .sportsgram-bracket-light [data-is-third-place="true"]::after {
            content: none !important;
            display: none !important;
          }
          /* Улучшаем соединение линий с карточками матчей */
          .sportsgram-bracket-light [class*="seed"] {
            position: relative;
            z-index: 1;
            background: transparent !important;
            /* Базовая ширина - будет переопределена через JavaScript */
            min-width: 180px !important;
            flex: 0 0 auto !important;
            box-sizing: border-box !important;
          }
          /* Все seed в одной колонке (раунде) должны иметь одинаковую ширину */
          .sportsgram-bracket-light [class*="round"] {
            display: flex !important;
            flex-direction: column !important;
            align-items: stretch !important;
            width: auto !important;
            min-width: fit-content !important;
            flex-shrink: 0 !important;
            flex-grow: 0 !important;
            /* Колонка адаптируется под содержимое */
            align-self: flex-start !important;
            /* Убираем любые ограничения по ширине */
            max-width: none !important;
            box-sizing: border-box !important;
          }
          /* Seed в раунде - ширина будет установлена через JavaScript для синхронизации */
          .sportsgram-bracket-light [class*="round"] [class*="seed"] {
            flex: 0 0 auto !important;
            box-sizing: border-box !important;
            /* Ширина будет установлена через JavaScript для синхронизации */
          }
          
          /* Принудительная синхронизация ширины seed в раунде через data-атрибут */
          .sportsgram-bracket-light [class*="round"] [class*="seed"][data-synced-width] {
            width: var(--round-seed-width, auto) !important;
            min-width: var(--round-seed-width, auto) !important;
            max-width: var(--round-seed-width, auto) !important;
            flex: 0 0 auto !important;
            box-sizing: border-box !important;
          }
          
          /* Дополнительное правило для всех seed в раунде с синхронизированной шириной */
          .sportsgram-bracket-light [class*="round"][style*="--round-seed-width"] [class*="seed"] {
            width: var(--round-seed-width) !important;
            min-width: var(--round-seed-width) !important;
            max-width: var(--round-seed-width) !important;
            flex: 0 0 auto !important;
            box-sizing: border-box !important;
          }
          
          /* Принудительная синхронизация: все seed в одном раунде должны иметь одинаковую ширину */
          /* Используем CSS для синхронизации ширины всех seed в раунде */
          .sportsgram-bracket-light [class*="round"] [class*="seed"] {
            /* Ширина будет установлена через JavaScript, но CSS обеспечивает базовую синхронизацию */
            flex: 0 0 auto !important;
            box-sizing: border-box !important;
          }
          
          /* Максимально агрессивное правило: все seed в раунде с data-synced-width должны иметь одинаковую ширину */
          /* Используем прямое значение из атрибута для максимальной надежности */
          .sportsgram-bracket-light [class*="round"] [class*="seed"][data-synced-width] {
            width: var(--round-seed-width) !important;
            min-width: var(--round-seed-width) !important;
            max-width: var(--round-seed-width) !important;
            flex: 0 0 var(--round-seed-width) !important;
            box-sizing: border-box !important;
          }
          
          /* Переопределяем любые inline стили библиотеки через более специфичный селектор */
          .sportsgram-bracket-light [class*="bracket"] [class*="round"] [class*="seed"][data-synced-width][style] {
            width: var(--round-seed-width) !important;
            min-width: var(--round-seed-width) !important;
            max-width: var(--round-seed-width) !important;
            flex: 0 0 var(--round-seed-width) !important;
          }
          
          /* Дополнительное правило через CSS переменную seed */
          .sportsgram-bracket-light [class*="round"] [class*="seed"][style*="--seed-width"] {
            width: var(--seed-width) !important;
            min-width: var(--seed-width) !important;
            max-width: var(--seed-width) !important;
            flex: 0 0 var(--seed-width) !important;
            box-sizing: border-box !important;
          }
          
          /* Максимальная специфичность: все seed в раунде должны иметь одинаковую ширину */
          .sportsgram-bracket-light [class*="bracket"] [class*="round"] [class*="seed"][data-synced-width] {
            width: var(--round-seed-width) !important;
            min-width: var(--round-seed-width) !important;
            max-width: var(--round-seed-width) !important;
            flex: 0 0 var(--round-seed-width) !important;
            box-sizing: border-box !important;
          }
          
          /* Убеждаемся, что все seed-team элементы занимают 100% ширины родителя */
          .sportsgram-bracket-light [class*="seed-team"] {
            width: 100% !important;
            min-width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            position: relative;
            z-index: 2;
            /* Фиксированная высота для единообразия */
            min-height: 60px !important;
            height: 60px !important;
            display: flex !important;
            align-items: center !important;
          }
          
          /* Дополнительное правило для текстовых элементов внутри seed-team */
          .sportsgram-bracket-light [class*="seed-team"] > div {
            max-width: 100% !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
          /* Увеличиваем точность позиционирования линий */
          /* Линии должны быть ВЫШЕ карточек, иначе горизонтальные сегменты скрываются */
          .sportsgram-bracket-light svg {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 5;
          }
          /* Обеспечиваем правильное соединение линий с центрами SeedTeam */
          .sportsgram-bracket-light [class*="seed-item"] {
            position: relative;
          }
          /* Улучшаем точность соединения линий - линии должны доходить до центров SeedTeam */
          .sportsgram-bracket-light [class*="bracket"] [class*="round"] {
            position: relative;
            display: flex !important;
            flex-direction: column !important;
            align-items: stretch !important;
            width: auto !important;
            min-width: fit-content !important;
          }
          /* Все seed в одной колонке (раунде) должны иметь одинаковую ширину - растягиваются на всю ширину колонки */
          /* НО: если установлена синхронизированная ширина, используем её */
          .sportsgram-bracket-light [class*="bracket"] [class*="round"] [class*="seed"]:not([data-synced-width]) {
            width: 100% !important;
            flex: 0 0 auto !important;
            max-width: none !important;
          }
          /* Убеждаемся, что линии проходят через правильные точки */
          .sportsgram-bracket-light [class*="seed-team"]:first-child {
            margin-bottom: 4px;
          }
          /* Увеличиваем длину линий, чтобы они доходили до краев карточек */
          .sportsgram-bracket-light svg line[stroke="#8c8c8c"] {
            stroke-linecap: round !important;
          }
          /* Обеспечиваем правильное позиционирование для расчета линий библиотекой */
          .sportsgram-bracket-light [class*="seed-team"] {
            min-height: 60px !important;
            height: 60px !important;
            box-sizing: border-box !important;
            position: relative !important;
            /* Убираем лишние отступы, которые могут влиять на расчет центров */
            margin: 0 !important;
            /* Адаптивная ширина - блоки растягиваются на всю ширину seed */
            width: 100% !important;
            max-width: none !important;
            overflow: visible !important;
            white-space: normal !important;
            display: flex !important;
            align-items: center !important;
          }
          /* Первый SeedTeam должен иметь отступ снизу для визуального разделения */
          .sportsgram-bracket-light [data-team-index="0"] {
            border-top-left-radius: 4px;
            border-top-right-radius: 4px;
            margin-bottom: 4px !important;
          }
          .sportsgram-bracket-light [data-team-index="1"] {
            border-bottom-left-radius: 4px;
            border-bottom-right-radius: 4px;
            margin-top: 0 !important;
          }
          /* Увеличиваем точность расчета линий - линии должны доходить до центров SeedTeam */
          /* Примечание: это правило переопределено выше для обеспечения одинаковой ширины */
          /* Улучшаем соединение линий - линии должны проходить через центры SeedTeam */
          .sportsgram-bracket-light svg line {
            stroke-linecap: round !important;
            stroke-linejoin: round !important;
          }
          /* Увеличиваем область для расчета линий */
          .sportsgram-bracket-light [class*="seed-item"] {
            position: relative;
            overflow: visible !important;
            /* Обеспечиваем правильное выравнивание для расчета центров */
            display: flex !important;
            flex-direction: column !important;
            align-items: stretch !important;
          }
          /* Важно: SeedTeam должны иметь одинаковую структуру для правильного расчета центров */
          .sportsgram-bracket-light [class*="seed-team"] > * {
            box-sizing: border-box;
          }
          /* Не скрываем SVG/линии внутри финала и боя за 3 место — там входящие соединители (полуфинал → финал). Скрытие справа от колонки — в JS. */
          /* Не скрываем SVG/линии в последнем раунде — там входящие соединители. Скрытие только справа от колонки — в JS. */
          
          /* Скрываем линии между соседними seed в последнем раунде (между финалом и боем за 3 место) */
          .sportsgram-bracket-light [class*="round"]:last-child [class*="seed"] + [class*="seed"] {
            position: relative !important;
          }
          
          .sportsgram-bracket-light [class*="round"]:last-child [class*="seed"] + [class*="seed"]::before {
            content: '' !important;
            display: block !important;
            position: absolute !important;
            top: -20px !important;
            left: 0 !important;
            right: 0 !important;
            height: 1px !important;
            background: transparent !important;
            z-index: 1000 !important;
            pointer-events: none !important;
          }
          /* Скрытие SVG/линий только справа от последней колонки — в JS (hideFinalRoundLines). Здесь не скрываем. */
          
          /* КРИТИЧНО: Библиотека рисует линии через ::before и ::after у seed — отключаем исходящие в последнем раунде */
          .bracket-view-wrapper .sportsgram-bracket-light [class*="round"][data-last-round-no-lines="true"]::before,
          .bracket-view-wrapper .sportsgram-bracket-light [class*="round"][data-last-round-no-lines="true"]::after,
          .bracket-view-wrapper .sportsgram-bracket-light [class*="round"][data-last-round-no-lines="true"] [class*="seed"]::before,
          .bracket-view-wrapper .sportsgram-bracket-light [class*="round"][data-last-round-no-lines="true"] [class*="seed"]::after,
          .bracket-view-wrapper .sportsgram-bracket-light [class*="round"][data-last-round-no-lines="true"] > *::before,
          .bracket-view-wrapper .sportsgram-bracket-light [class*="round"][data-last-round-no-lines="true"] > *::after,
          .bracket-view-wrapper .sportsgram-bracket-light [class*="round"]:last-child [class*="seed"]::before,
          .bracket-view-wrapper .sportsgram-bracket-light [class*="round"]:last-child [class*="seed"]::after,
          .bracket-view-wrapper .sportsgram-bracket-light [class*="round"]:last-child > *::before,
          .bracket-view-wrapper .sportsgram-bracket-light [class*="round"]:last-child > *::after,
          .bracket-view-wrapper .sportsgram-bracket-light [class*="round"]:last-child [data-is-final="true"]::before,
          .bracket-view-wrapper .sportsgram-bracket-light [class*="round"]:last-child [data-is-final="true"]::after,
          .bracket-view-wrapper .sportsgram-bracket-light [class*="round"]:last-child [data-is-third-place="true"]::before,
          .bracket-view-wrapper .sportsgram-bracket-light [class*="round"]:last-child [data-is-third-place="true"]::after {
            display: none !important;
            content: none !important;
            width: 0 !important;
            height: 0 !important;
            border: none !important;
            background: transparent !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          
          /* КРИТИЧНО: Правильное позиционирование финала и боя за 3 место.
             Бой за 3 место — сверху/слева (order: -1), финал — снизу/справа (order: 0). */
          .sportsgram-bracket-light [class*="round"]:last-child {
            display: flex !important;
            flex-direction: column !important;
            align-items: stretch !important;
          }
          
          /* Обычная сетка: последний раунд в одну строку — бой за 3 место и финал рядом, внутри сетки */
          .standard-bracket .sportsgram-bracket-light [class*="round"]:last-child {
            flex-direction: row !important;
            align-items: flex-start !important;
            gap: 24px !important;
            overflow: visible !important;
            flex-shrink: 0 !important;
            min-width: 380px !important;
            align-self: flex-start !important;
            min-height: auto !important;
          }
          /* Контейнер сидов последнего раунда — flex в ряд, чтобы оба матча были в сетке */
          .standard-bracket .sportsgram-bracket-light [class*="round"]:last-child > * {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            gap: 24px !important;
            position: relative !important;
            min-height: auto !important;
          }
          .standard-bracket .sportsgram-bracket-light [class*="round"]:last-child [data-is-final="true"],
          .standard-bracket .sportsgram-bracket-light [class*="round"]:last-child [data-is-third-place="true"] {
            margin-bottom: 0 !important;
            margin-top: 0 !important;
            flex-shrink: 0 !important;
            position: relative !important;
            top: auto !important;
            left: auto !important;
          }
          
          .sportsgram-bracket-light [class*="round"]:last-child [data-is-final="true"],
          .sportsgram-bracket-light [class*="round"]:last-child [data-is-third-place="true"] {
            position: relative !important;
          }
          
          /* Визуальный разделитель между финалом и боем за 3 место (только для сплит/колонки) */
          .split-bracket-container .sportsgram-bracket-light [class*="round"]:last-child [data-is-final="true"] {
            margin-bottom: 24px !important;
          }
          
          .sportsgram-bracket-light [class*="round"]:last-child [data-is-third-place="true"] {
            margin-top: 0 !important;
          }
          
          /* Большой отступ между финалом и боем за 3 место — только для сплит (колоночная раскладка) */
          .split-bracket-container .sportsgram-bracket-light [class*="round"]:last-child [data-is-final="true"] {
            margin-bottom: 56px !important;
            padding-bottom: 0 !important;
          }
          
          .sportsgram-bracket-light [class*="round"]:last-child [data-is-third-place="true"] {
            padding-top: 0 !important;
          }
          
          /* Разделитель между финалом и боем за 3 место — только в колоночной раскладке */
          .split-bracket-container .sportsgram-bracket-light [class*="round"]:last-child [data-is-final="true"]::after {
            content: '' !important;
            display: block !important;
            height: 2px !important;
            width: calc(100% + 80px) !important;
            background: linear-gradient(to right, transparent, #e8e8e8, transparent) !important;
            margin: 20px -40px !important;
            position: relative !important;
            left: 0 !important;
            pointer-events: none !important;
            z-index: 1 !important;
          }
          
          .standard-bracket .sportsgram-bracket-light [class*="round"]:last-child [data-is-final="true"]::after {
            display: none !important;
          }
          
          /* У боя за 3 место — белый «заслон» сверху (только в колоночной раскладке) */
          .split-bracket-container .sportsgram-bracket-light [class*="round"]:last-child [data-is-third-place="true"]::before {
            content: '' !important;
            display: block !important;
            position: absolute !important;
            top: -60px !important;
            left: -60px !important;
            right: -60px !important;
            height: 60px !important;
            background: #fff !important;
            z-index: 5 !important;
            pointer-events: none !important;
          }
          
          .split-bracket-container .sportsgram-bracket-light [class*="round"]:last-child [data-is-final="true"] ~ [data-is-third-place="true"],
          .split-bracket-container .sportsgram-bracket-light [class*="round"]:last-child [data-is-third-place="true"] ~ [data-is-final="true"] {
            margin-top: 56px !important;
          }
          
          /* Убираем все линии в последнем раунде: псевдоэлементы у раунда и у seed (классы библиотеки sc-*, без "seed") */
          [data-last-round-no-lines="true"]::before,
          [data-last-round-no-lines="true"]::after,
          [data-last-round-no-lines="true"] *::before,
          [data-last-round-no-lines="true"] *::after,
          [data-last-round-no-lines="true"] [data-is-third-place="true"]::before,
          [data-last-round-no-lines="true"] [data-is-third-place="true"]::after,
          [data-last-round-no-lines="true"] [data-is-final="true"]::before,
          [data-last-round-no-lines="true"] [data-is-final="true"]::after {
            display: none !important;
            content: none !important;
            width: 0 !important;
            height: 0 !important;
            min-width: 0 !important;
            max-width: 0 !important;
            border: none !important;
            border-width: 0 !important;
            background: transparent !important;
            padding: 0 !important;
            margin: 0 !important;
            box-shadow: none !important;
          }
          
          /* Не скрываем SVG внутри финала/бья за 3 место — там входящие соединители. Скрытие только справа — в JS. */
        `}</style>
        {isSplitBracket ? (
          <div className="split-bracket-container">
            <StyleSheetManager shouldForwardProp={shouldForwardProp}>
              <Bracket
                rounds={splitAllRounds}
                renderSeedComponent={CustomSeed}
                roundTitleComponent={roundTitleComponent}
                bracketClassName="sportsgram-bracket-light"
              />
            </StyleSheetManager>
          </div>
        ) : null}
        
        {!isSplitBracket && (
          <div className="standard-bracket">
            <StyleSheetManager shouldForwardProp={shouldForwardProp}>
              <Bracket
                rounds={rounds}
                renderSeedComponent={CustomSeed}
                roundTitleComponent={roundTitleComponent}
                bracketClassName="sportsgram-bracket-light"
              />
            </StyleSheetManager>
          </div>
        )}
      </div>
    </Card>
  );
};

export default ClassicBracketView;
